export type UiLocale = 'ja' | 'en'

export const UI_LOCALE_OPTIONS: { code: UiLocale; label: string }[] = [
  { code: 'ja', label: '日本語' },
  { code: 'en', label: 'English' },
]

const en = {
  teamsCalendarAllMeetings: 'Teams meetings',
  teamsCalendarOrganizer: 'Organizer',
  teamsCalendarScheduledTime: 'Scheduled time',
  teamsCalendarHint: 'Microsoft Teams meetings for the next 7 days.',
  teamsCalendarConnectHint: 'Connect your Microsoft account to show your Teams calendar here.',
  teamsCalendarReconnectHint: 'Calendar access has expired or was denied. Reconnect your Microsoft account.',
  teamsCalendarConnect: 'Connect Microsoft Calendar',
  teamsCalendarReconnect: 'Reconnect Microsoft Calendar',
  teamsCalendarDisconnect: 'Disconnect Calendar',
  teamsCalendarRefresh: 'Refresh Calendar',
  teamsCalendarJoin: 'Join in Teams',
  teamsCalendarLoading: 'Loading Teams calendar...',
  teamsCalendarLoadFailed: 'Unable to load Teams calendar. Please try again.',
  teamsCalendarConsentFailed: 'Unable to connect Microsoft Calendar. Please try again and allow calendar access.',
  googleCalendarAllMeetings: 'Google Meet meetings',
  googleCalendarHint: 'Google Meet meetings for the next 7 days.',
  googleCalendarConnectHint: 'Sign in with Google and allow calendar access to show your Google Meet meetings here.',
  googleCalendarReconnectHint: 'Calendar access has expired or was denied. Sign in with Google again to restore it.',
  googleCalendarConnect: 'Connect Google Calendar',
  googleCalendarReconnect: 'Reconnect Google Calendar',
  googleCalendarRefresh: 'Refresh Calendar',
  googleCalendarJoin: 'Join in Google Meet',
  googleCalendarLoading: 'Loading Google Meet calendar...',
  googleCalendarLoadFailed: 'Unable to load Google Meet calendar. Please try again.',
  googleCalendarConsentFailed: 'Unable to connect Google Calendar. Please try again and allow calendar access.',
  // Meeting brief (Upcoming)
  briefView: 'View brief',
  briefButton: 'Brief',
  briefCloseDialog: 'Close meeting brief',
  briefModalTitle: 'Meeting brief · {meeting}',
  briefNearest: 'Nearest',
  briefAiEmpty: 'No brief yet · pick a project to start',
  briefAiGenerating: 'Creating a brief for {project}...',
  briefAiPreparing: 'AI is preparing a brief from the calendar title…',
  briefAiGenerated: 'AI summarized at {time} from the {date} meeting and Backlog',
  briefAiFailed: 'Could not generate the brief',
  briefProjectLabel: 'Project',
  briefProjectPlaceholder: 'Select a project...',
  briefProjectSearch: 'Search projects...',
  briefProjectNoResults: 'No project matches that search.',
  briefProjectSuggested: 'Suggested from the meeting name',
  briefProjectHint: 'AI infers the project from the calendar title. Change it here if that is wrong.',
  briefRefresh: 'Refresh',
  briefEmptyTitle: 'No brief yet',
  briefEmptyBody:
    'Pick a project above and AI will pull the previous meeting summary, Backlog ticket status, and related comments for this meeting.',
  briefStepProject: 'Selected project {project}',
  briefStepMinutes: 'Read the {date} meeting summary',
  briefStepTickets: 'Fetching Backlog tickets for the project...',
  briefStepSynthesize: 'Synthesizing the brief',
  briefLoading: 'Preparing the meeting brief...',
  briefAgendaTitle: 'Agenda',
  briefAgendaSectionProgress: 'Progress',
  briefAgendaSectionDecisions: 'Decisions needed',
  briefAgendaSectionQuestions: 'Suggested Q&A',
  briefAgendaSectionRisks: 'Risks',
  briefPreviousMeeting: 'Previous meeting summary',
  briefOpenMinutes: 'Open minutes',
  briefMinutesAccessDenied:
    '{title} has not been shared with you. Please contact the owner {ownerName} ({ownerEmail}) to request view access.',
  briefMinutesAccessDeniedNoOwner:
    '{title} has not been shared with you. Please contact the meeting owner to request view access.',
  briefMeetingMeta: 'Meeting #{number} · {title} · {date} · {duration} min',
  briefMeetingMetaPlain: '{title} · {date} · {duration} min',
  briefPreparing: 'Preparing a brief for {project}...',
  briefAiSkipped: 'No previous meeting with minutes to brief yet',
  briefNoBriefableTitle: 'No previous meeting to brief',
  briefNoBriefableBody:
    'This project has no meeting with minutes that can be briefed yet. The popup will stay empty until one exists.',
  briefAgreed: 'Agreed',
  briefAssigned: 'Assigned',
  briefAssignedCreated: '{count} tasks · tickets created on Backlog (see below)',
  briefAssignedCreatedOne: '1 task · ticket created on Backlog (see below)',
  briefAssignedNotCreated: '{count} tasks · not created on Backlog yet',
  briefAssignedNotCreatedOne: '1 task · not created on Backlog yet',
  briefBacklogTitle: 'Backlog ticket status',
  briefTicketCount: '{count} tickets from the previous meeting',
  briefTicketCountOne: '1 ticket from the previous meeting',
  briefBacklogBreakdown: '{resolved} Resolved · {progress} In Progress · {open} Open',
  briefDueToday: 'Due today',
  briefDueInDays: 'Due {date} · {count} days left',
  briefDueTomorrow: 'Due {date} · 1 day left',
  briefDueOverdue: 'Due {date} · {count} days overdue',
  briefDueOverdueOne: 'Due {date} · 1 day overdue',
  briefDoneOn: 'Done {date}',
  briefCommentsTitle: 'Related comment summary',
  briefCommentsHint: 'New comments on the tickets above since the previous meeting',
  briefNoComments: 'No new comments',
  briefCommentCount: '{count} comments',
  briefCommentCountOne: '1 comment',
  briefCommentMeta: '{comments} · {people} · {when}',
  briefNoTickets: 'No Backlog tickets linked to this project yet.',
  briefErrorBody: 'The brief could not be generated. Check your connection and try again.',
  briefRetry: 'Try again',
  briefStartsInMinutes: 'starts in {count} min',
  briefStartsInHour: 'starts in 1 hr',
  briefStartsInHours: 'starts in {hours} hrs',
  briefStartsInHourMinutes: 'starts in 1 hr {minutes} min',
  briefStartsInHoursMinutes: 'starts in {hours} hr {minutes} min',
  briefStartsInDay: 'starts in 1 day',
  briefStartsInDays: 'starts in {count} days',
  briefStartingNow: 'starting now',
  briefToday: 'today',
  briefYesterday: 'yesterday',
  briefDaysAgo: '{count} days ago',
  // Common
  edit: 'Edit',
  cancel: 'Cancel',
  save: 'Save',
  saveChanges: 'Save changes',
  start: 'Start',
  delete: 'Delete',
  deleting: 'Deleting...',
  view: 'View',
  share: 'Share',
  shareMeetingTitle: 'Share meeting',
  searchByNameOrEmail: 'Search by name or email',
  nSelected: '{count} selected',
  continueAction: 'Continue',
  confirmShareTitle: 'Confirm share',
  minutesTranscriptAndAudio: 'Minutes, transcript, and audio',
  recipientsCanViewDisclaimer:
    'Recipients can view the full content and share it with others.',
  meetingSharedTitle: 'Meeting shared',
  meetingSharedBody: '{meetingName} is now shared with {names}.',
  shareRecipientsAlreadyHaveAccess:
    'All selected recipients already have access to this meeting.',
  shareRecipientNeedsAccount: 'No BrSE.ai account yet',
  shareDirectoryUnavailable:
    'Your Microsoft tenant directory is unavailable. Try again later.',
  shareDirectoryNoMatch: 'Nobody matches that search.',
  currentlySharedWith: 'Currently shared with',
  removedSharesSection: 'REMOVED',
  revoke: 'Revoke',
  removeAccess: 'Remove access',
  revokeShareTitle: 'Remove {name} from this meeting?',
  revokeShareBody:
    'They — and anyone they shared this with — will lose access.',
  alreadySharedLabel: 'Already shared',
  // BRAI-533 — sharing a meeting live, from the create form.
  shareWithMembersLabel: 'Share with',
  shareWithMembersHint:
    'Invite other members to join this meeting on BrSE.ai to improve translation accuracy.',
  shareWithMembersSearch: 'Search members by name or email',
  shareWithMembersPlaceholder: 'Nobody yet',
  /** Closed-control summary once the picked list is too long to spell out. */
  shareWithMembersSummary: '{names} +{count}',
  shareWithMembersOpen: 'Choose who to share with',
  shareWithMembersClear: 'Clear all',
  shareWithMembersEmpty: 'Nobody matches that search.',
  shareOnCreateFailed:
    'The meeting started, but sharing failed. You can share it from History.',
  /** Live meeting header — open invite modal (same picker as Create Meeting). */
  liveInviteButton: 'Invite',
  liveInviteTitle: 'Invite people',
  liveInviteHint:
    'Invite people to join {meeting} with you to improve translation quality.',
  liveInviteSubmit: 'Send Invite',
  liveInviteSending: 'Sending…',
  liveInviteClose: 'Close invite dialog',
  liveInviteSent: 'Invite sent to {count} people.',
  liveInviteInThisRoom: 'In this room',
  liveInviteWaiting: 'Waiting',
  liveInviteInvitedHeading: 'Invited ({count})',
  meetingInviteBody: '{name} invited you to {meetingName}.',
  meetingInviteTitle: 'Meeting invitation',
  meetingInviteModalAfterName: ' invited you to join ',
  meetingInviteModalAfterMeeting:
    '. Do you want to join with the others to improve translation quality?',
  meetingInviteYes: 'Yes',
  meetingInviteNo: 'No',
  meetingInviteJoin: 'Join',
  meetingInviteChecking: 'Checking…',
  meetingInviteEnded: 'That meeting has already ended.',
  meetingInviteDismiss: 'Dismiss',
  joiningSharedMeetingTitle: 'Joining a shared meeting',
  joiningSharedMeetingHint:
    'You are joining a meeting someone shared with you. Share this tab’s audio so everyone hears the same call — the translation is paid for once and shared with the room.',
  accessRemoved: 'Access removed',
  removeFromHistory: 'Remove',
  removeFromHistoryTitle: 'Remove this meeting from your History?',
  removeFromHistoryBody:
    "This only hides it from your list. It won't remove anyone's access.",
  shareDisabledNoContent:
    'Nothing to share yet — add transcript, minutes, or audio first.',
  shareDisabledProcessing: 'Content is still being processed.',
  shareDisabledError: 'Sharing is unavailable for this meeting.',
  failedRevokeShare: 'Failed to revoke share.',
  failedHideMeeting: 'Failed to hide meeting.',
  removedOnDate: 'Removed {date}',
  meetingSharedWithOthers: '{names} and {count} others',
  sharedWithCol: 'Shared with',
  sharedByCol: 'Shared by',
  nPeople: '{count} people',
  onePerson: '1 person',
  back: 'Back',
  failedShareMeetingService: 'Failed to share meeting.',
  export: 'Export',
  hide: 'Hide',
  show: 'Show',
  close: 'Close',
  completed: 'Completed',
  mins: 'mins',
  min: 'min',
  minutes: 'minutes',
  minute: 'minute',
  loading: 'Loading...',
  saving: 'Saving...',
  required: 'Required',
  fieldTooLong: 'Must be {max} characters or fewer.',
  pending: 'Pending',
  clear: 'Clear',
  previousMonth: 'Previous month',
  nextMonth: 'Next month',

  // App version banner
  newVersionAvailable: 'New version available. Click to reload.',
  reloadNow: 'Reload',

  // User menu
  logout: 'Log out',
  lightMode: 'Light mode',
  darkMode: 'Dark mode',
  language: 'Language',
  exportingReport: 'Exporting...',
  exportReportError: 'Failed to export report',

  // Admin management screen
  adminManagement: 'Administration',
  adminTabUsers: 'Users',
  adminTabLoginLogs: 'Login history',
  adminTabActivity: 'Feature usage',
  adminTabMeetings: 'Meetings',
  adminDownloadExcel: 'Download Excel',
  adminLoadError: 'Failed to load data',
  adminEmpty: 'No data',
  adminSearchUsers: 'Search by email or name',
  adminSearchLoginLogs: 'Search by email, name, IP or method',
  adminSearchMeetings: 'Search by title or email',
  adminFilterMethod: 'Method',
  adminFilterMethodAll: 'All methods',
  adminFilterDateFrom: 'From',
  adminFilterDateTo: 'To',
  adminClearFilters: 'Clear filters',
  adminColEmail: 'Email',
  adminColName: 'Name',
  adminColFunction: 'Function',
  adminColUses: 'Uses',
  adminColSucceeded: 'Succeeded',
  adminColFailed: 'Failed',
  adminColDistinctUsers: 'People',
  adminActivityPeopleButton: 'Show the {count} people who used {label}',
  adminActivityPeopleTitle: 'Who used this',
  adminActivityFailuresButton: 'Show the {count} failures of {label}',
  adminActivityFailuresTitle: 'Failures',
  adminActivityNoMeeting: 'No meeting',
  adminColArea: 'Area',
  adminColActivity: 'Activity',
  adminViewActivity: 'View',
  adminUserActivityTitle: 'Activity log',
  adminColStatus: 'Status',
  adminColMeeting: 'Meeting',
  adminColProject: 'Project',
  adminColWhen: 'When',
  adminStatusSuccess: 'Success',
  adminStatusFailure: 'Failure',
  adminFilterFunction: 'Function',
  adminFilterFunctionAll: 'All functions',
  adminColRole: 'Role',
  adminColProvider: 'Provider',
  adminColCreatedAt: 'Created',
  adminColLoginCount: 'Logins',
  adminColLastLogin: 'Last login',
  adminColMeetingCount: 'Meetings',
  adminColTotalMinutes: 'Total minutes',
  adminColAvgRating: 'Avg rating',
  adminColMethod: 'Method',
  adminColIpAddress: 'IP address',
  adminColUserAgent: 'User agent',
  adminColLoginAt: 'Login at',
  adminColTitle: 'Title',
  adminColMeetingId: 'Meeting ID',
  adminColDuration: 'Duration (hh:mm:ss)',
  adminColRating: 'Rating',
  adminColComment: 'Comment',
  adminColCurrency: 'Currency',
  adminColCost: 'Cost (USD)',
  adminPrevPage: 'Previous',
  adminNextPage: 'Next',
  adminPageInfo: 'Page {page} of {totalPages}',
  adminTotalRows: '{total} rows',
  adminTabFeedback: 'Feedback',
  adminSearchFeedback: 'Search by name or comment',
  adminTabAllowedEmails: 'Sign-in allowlist',
  adminSearchAllowedEmails: 'Search by email or note',
  allowedEmailsHelp:
    'Staff on the company domains can always sign in. Everyone else needs their address on this list — adding one here is what lets a customer sign in with Outlook or Google.',
  allowedEmailAddress: 'Email address',
  allowedEmailPickPlaceholder: 'Select an address…',
  allowedEmailSearchDirectory: 'Search by name or address',
  allowedEmailTenantGroup: 'In your tenant',
  allowedEmailNoDirectoryMatch: 'Nobody matches that.',
  allowedEmailEnterManually: 'Another address (outside the tenant)…',
  allowedEmailOtherAddress: 'Email address outside the tenant',
  allowedEmailBackToList: 'Back to list',
  allowedEmailAddressRequired: 'Choose an address first.',
  allowedEmailDirectoryHint: '{count} addresses from your Microsoft tenant.',
  allowedEmailNote: 'Note',
  allowedEmailNotePlaceholder: 'e.g. ABC Corp — pilot',
  allowedEmailAdd: 'Allow',
  allowedEmailAdding: 'Allowing…',
  allowedEmailAdded: 'That address can now sign in.',
  allowedEmailAddFailed: 'Could not allow that address. Please try again.',
  allowedEmailRemove: 'Revoke',
  allowedEmailRemoved: 'That address can no longer sign in.',
  allowedEmailRemoveFailed: 'Could not revoke that address. Please try again.',
  allowedEmailRemoveConfirm: 'Revoke sign-in access for {email}?',
  allowedEmailColAddedBy: 'Added by',
  allowedEmailColAddedAt: 'Added',
  adminColFeedbackAt: 'Feedback date',

  // Sidebar / nav
  closeSidebar: 'Close sidebar',
  hideSidebar: 'Hide sidebar',
  showSidebar: 'Show sidebar',
  home: 'Home',
  history: 'History',
  settings: 'Settings',
  settingsGroupGeneral: 'General',
  settingsGroupIntegrations: 'Integrations',
  settingsAppearanceNav: 'Language & appearance',
  settingsLanguageTitle: 'Interface language',
  settingsLanguageDescription: 'Choose the language brSE.ai is displayed in.',
  settingsThemeTitle: 'Display mode',
  settingsThemeDescription: 'Choose a light or dark interface.',
  settingsBacklogCardTitle: 'Backlog',
  settingsBacklogPushDescription:
    'Action items from your minutes are pushed to Backlog using the account below.',
  settingsBacklogConnectedSince: 'Connected since {date}',
  settingsBacklogDisconnect: 'Disconnect',
  settingsBacklogDisconnecting: 'Disconnecting…',
  settingsBacklogDisconnectConfirm:
    'Disconnect this Backlog account? Pushing action items will stop working until you connect one again.',
  settingsBacklogDisconnectNote:
    'This ends brSE.ai’s own access. The approval also exists in Backlog and is removed there.',
  settingsBacklogStatusConnected: 'Connected',
  settingsBacklogStatusNotConnected: 'Not connected',
  settingsBacklogCapabilities: 'What brSE.ai will do',
  settingsBacklogCanProjects: 'See the list of projects you belong to',
  settingsBacklogCanTickets: 'Create and update tickets when you press Push to Backlog',
  settingsBacklogCannot: 'Never see your password, and never push anything you have not confirmed',
  trash: 'Trash',
  startMeeting: 'Start Meeting',
  createMeeting: 'Create Meeting',

  // Glossary page
  glossarySystemDictionaries: 'System dictionaries',
  glossaryYourDictionaries: 'Your dictionaries',
  glossaryTermsLabel: 'terms',
  glossarySystemLabel: 'System',
  glossaryUploadedLabel: 'Uploaded',
  glossaryDownload: 'Download',
  glossaryDownloadTemplate: 'Download template',
  glossaryUploadDictionary: 'Upload dictionary',
  glossaryUpload: 'Upload',
  glossaryUploading: 'Uploading...',
  glossaryUploadConstraints: '.csv only · Max 5MB · Max 2,000 rows · 2 columns required',
  glossaryUploadCsvOnly: 'Only .csv files are allowed.',
  glossaryUploadTooLarge: 'File must be 5MB or smaller.',
  glossaryUploadFailed: 'Failed to upload dictionary. Please try again.',
  glossaryUploadDragDrop: 'Drag & drop or click to select files',
  glossaryUploadProgress: 'Uploading {current} of {total}...',
  glossaryUploadFilesSkipped:
    'Files marked in red will be skipped. Remove or replace them, or continue with the valid files.',
  glossaryUploadFile: 'Upload {count} file',
  glossaryUploadFiles: 'Upload {count} files',
  glossaryUploadNotCsv: 'Not a .csv file',
  glossaryUploadFileTooLarge: 'File too large',
  glossaryUploadSummary: '{uploaded} of {total} files uploaded',
  glossaryUploadSummaryHint: 'Fix the files below and upload again.',
  glossaryUploadTermsAdded: '{count} terms added',
  glossaryUploadRowsSkipped: '{count} rows skipped (too long)',
  glossaryUploadRowSkipped: '1 row skipped (too long)',
  glossaryUploadTermsConflict: '{count} terms conflict',
  glossaryUploadTermConflict: '1 term conflicts',
  glossaryUploadTooManyRows: 'Too many rows — max 2,000',
  glossaryUploadWrongFormat: 'Wrong format',
  glossaryUploadEmptyCells: 'Some cells are empty',
  glossaryUploadEmptyFile: 'No valid terms found',
  glossaryUploadViewDetails: 'View details',
  glossaryUploadHideDetails: 'Hide details',
  glossaryUploadChooseAnother: 'Choose another file',
  glossaryUploadDone: 'Done',
  glossaryUploadRowDetail: 'Row {row} · {field}: {reason}',
  glossaryUploadConflictDetail: '{term} · rows {rows} · meanings: {meanings}',
  glossaryUploadColumnExpected:
    'Column {column} is named “{actual}” instead of “{expected}”.',
  glossaryUploadColumnCount:
    'Expected {expected} columns but found {actual}.',
  glossaryUploadReasonTooLong: 'more than 100 characters',
  glossaryUploadFieldTerm: 'Language 1',
  glossaryUploadFieldMeaning: 'Language 2',
  glossaryRenameFailed: 'Could not rename this dictionary. Check the name and try again.',
  glossaryReorderHint:
    'Click the pencil icon to rename. Drag or use the arrows to reorder priority. Turn on Default to auto-select it for new meetings — you can still change this per meeting.',
  glossaryDefaultLabel: 'Default',
  glossaryEmptyUser: 'No dictionaries yet. Upload a CSV to get started.',
  glossaryRename: 'Rename dictionary',
  glossaryMoveUp: 'Move up',
  glossaryMoveDown: 'Move down',
  glossaryDragHandle: 'Drag to reorder',
  glossaryDeleteConfirm: 'Delete this dictionary?',
  glossaryActionFailed: 'Something went wrong. Please try again.',

  // Home
  myMeetings: 'My Meetings',
  hello: 'Hello, {name}!',
  ongoingMeetings: 'Ongoing Meetings',
  /** Badge on an ongoing-meeting card. Short on purpose: it sits beside a truncated title. */
  meetingLiveBadge: 'Live',
  /** Subtitle of an ongoing-meeting card: who it is that invited this reader. */
  meetingInviteFrom: 'Invited by {name}',
  recentMeetings: 'Recent Meetings',
  viewAll: 'View all →',
  upcoming: 'Upcoming',
  upcomingSectionTitle: 'Upcoming',
  upcomingBadge: 'UPCOMING',
  noMeetingsYet: 'No meetings yet',
  noMeetingsHint: 'Create a meeting to start translating and generating minutes.',
  noUpcomingMeetings: 'No upcoming meetings',
  noUpcomingMeetingsHint: 'Scheduled meetings will appear here when they are ready to start.',
  hasMinutes: 'Has minutes',
  noMinutes: 'No minutes',
  failedLoadMeetings: 'Failed to load meetings',
  tabAudioCaptureFailed: 'Unable to start tab audio capture. Please try again.',

  // Create meeting modal
  createNewMeeting: 'Create New Meeting',
  createMeetingSubtitle: 'Set up your real-time translation environment.',
  closeCreateMeetingModal: 'Close create meeting modal',
  meetingName: 'Meeting Name',
  meetingNameRequired: 'Meeting name is required.',
  meetingNameTooLong: 'Meeting name must be 255 characters or fewer.',
  meetingJoinUrlRequired: 'Meeting join URL is required for native transcription.',
  meetingJoinUrlLabel: 'Meeting join URL',
  meetingJoinUrlHint:
    'Native mode identifies participants from the meeting roster. The bot must be admitted to the meeting.',
  transcriptionSourceLabel: 'Transcription source',
  transcriptionSourceBrowser: 'Browser audio',
  transcriptionSourceTeams: 'Microsoft Teams',
  transcriptionSourceGoogleMeet: 'Google Meet',
  // The three numbered steps of the Create Meeting form.
  createMeetingStepDetails: 'Meeting details',
  createMeetingStepType: 'Meeting type',
  createMeetingStepLanguage: 'Language',
  // What the meeting listens to, as cards. "Offline" and "Online" are both a
  // browser-captured meeting; what separates them is whether a tab is shared,
  // which is what the pair of checkboxes under the microphone used to ask.
  meetingTypeOffline: 'Offline meeting',
  meetingTypeOfflineDesc: 'AI listens through your microphone',
  meetingTypeOnline: 'Online meeting',
  meetingTypeOnlineDesc: 'Share a tab, window or screen with audio',
  meetingTypeTeamsDesc: 'AI bot joins via the meeting link',
  meetingTypeGoogleMeetDesc: 'AI bot joins via the meeting link',
  shareTabAudioCallout:
    'After you press Start, choose the tab, window, or screen to share. Then turn on Share tab audio (or Share system audio) so BrSE.ai can hear and translate your meeting.',
  createMeetingHelpTitle: 'Help BrSE.ai understand your meeting',
  createMeetingHelpOptional: '(optional)',
  createMeetingHelpSubtitle:
    'Add names, roles, and terms to improve translation and meeting minutes.',
  transcriptionSourceGoogleMeetPreviewRequired:
    'Requires Google Meet Media API Developer Preview',
  connectGoogleMeetMedia: 'Connect Google Meet Media',
  meetingNameTaken: 'A meeting with this name already exists. Please choose a different name.',
  meetingNameTakenInTrash:
    'A meeting in the trash is using this name. Restore it, delete it permanently, or choose a different name.',
  meetingNamePlaceholder: 'e.g. Meeting#3',
  meetingNameResolving: 'Generating a name…',
  defaultMeetingName: 'Meeting#{n}',
  // BRAI-416 — what the session is for. A select rather than cards: it sits
  // above the translation-direction cards in an already-long form, and two
  // card groups in a row made it longer still.
  sessionKindLabel: 'Mode',
  sessionKindSelectionHint:
    'Select Interview mode for in-person interviews and Internal Meeting mode when you only need to record the meeting.',
  sessionKindMeeting: 'Normal Meeting',
  sessionKindInterview: 'Interview',
  sessionKindPresentation: 'Presentation',
  sessionKindInternalMeeting: 'Internal meeting',
  meetingModeMismatch:
    'The server did not confirm the selected mode. The meeting was stopped rather than recorded as a different kind.',
  minutesJobPollFailed: 'Could not check the minutes. Retrying…',
  internalMeetingNeedsTargetLang: 'Choose the meeting language before starting an internal meeting.',
  internalMeetingNoNativeMode: 'An internal meeting cannot be joined by the meeting bot.',
  internalMeetingNoSttProvider:
    'An internal meeting picks its own transcription engine — clear the selected one.',
  internalMeetingRecording: 'Recording. Nothing appears here until the meeting ends.',
  internalMeetingPaused: 'Paused. Nothing is being recorded.',
  internalMeetingQueuePending: 'Meeting ended. The minutes are queued…',
  internalMeetingQueueRunning: 'Writing the minutes…',
  internalMeetingTruncated:
    'The recording hit its limit, so these minutes cover only the first part of the meeting.',
  internalMeetingFailed: 'The minutes could not be produced.',
  internalMeetingExpired:
    'These minutes are no longer available. They are kept only briefly and cannot be produced again.',
  recordMeeting: 'Record Meeting',
  meetingLanguage: 'Meeting language',
  // Labels the one-way/two-way cards. Without it they sat unlabelled directly
  // under the Mode cards, in the same card styling, reading as one group of
  // four options rather than two separate settings.
  translationLabel: 'Translation',
  translateOneWay: 'Translate One-Way',
  translateOneWayDesc: "Interpret other participants' speech into your language",
  translateTwoWay: 'Translate Two-Way',
  translateTwoWayDesc: 'Interpret both sides of the conversation in real time',
  microphone: 'Microphone',
  defaultMicrophone: 'Default Microphone',
  microphoneN: 'Microphone {n}',
  microphoneLoadFailed: 'Unable to load microphone devices.',
  noMicrophoneDetected: 'No microphone detected — the meeting will run without one.',
  shareTabAudioHint:
    'I will select a browser tab and enable Share tab audio when the recording prompt opens.',
  /* AI DRAFT — needs a native-speaker review before release. */
  sendScreenFramesHint:
    'Also send a picture of the shared tab to the AI every few seconds, so it can read what is on screen',
  transcriptionLanguage: 'Transcription Language (Source)',
  translationLanguage: 'Translation Language (Target)',
  languageEnglish: 'English',
  languageJapanese: 'Japanese',
  languageVietnamese: 'Vietnamese',
  sttProviderSonioxLabel: 'Soniox',
  sttProviderSonioxDesc: 'Default',
  sttProviderAzureLabel: 'Azure',
  sttProviderAzureDesc: 'Microsoft Azure AI',
  sttProviderGoogleLabel: 'Google',
  sttProviderGoogleDesc: 'Gemini Live STT + LLM',
  sttProviderChatgptLabel: 'Azure OpenAI',
  sttProviderChatgptDesc: 'GPT Realtime Transcribe',
  autoDetect: 'Auto-detect',
  // Short badge fallback for the in-meeting header's compact language-pair
  // pill — kept distinct from `autoDetect` so English stays "AUTO" there.
  autoDetectBadge: 'AUTO',
  contextOptional: 'Context (Optional)',
  contextMeetingDescription: 'Meeting content description',
  contextFileDragDrop: 'Drag & drop or click to select file',
  contextFileSelect: 'Select file',
  contextFileFormats: 'PDF · DOCX · PPTX · XLSX · HTML · Max 20MB',
  contextFileAndOr: 'And / Or',
  contextFileInvalidType: 'Only PDF, DOCX, PPTX, XLSX, and HTML files are allowed.',
  contextFileTooLarge: 'Each file must be 20MB or smaller.',
  contextFileInvalidTypeAndSize:
    'Only PDF, DOCX, PPTX, XLSX, and HTML files up to 20MB are allowed.',
  contextAnalyzing: 'Analyzing context...',
  contextAnalyzeFailed: 'Unable to analyze meeting files. Please try again.',
  contextFileStatusDone: 'DONE',
  removeContextFile: 'Remove file',
  enterText: 'Enter Text',
  glossary: 'Glossary',
  upgradeNow: 'Upgrade Now',
  glossaryAiIt: 'AI and IT (applied)',
  glossarySelectPlaceholder: 'Select your dictionaries',
  glossarySystemAppliedAutomatically: 'IT dictionaries are applied automatically.',
  // Backlog project picker (BRAI-506)
  // ── Weekly report (BRAI-772) ──
  weeklyReportTab: 'Weekly Report',
  weeklyReportWeekLabel: 'Report week (Fri–Thu)',
  weeklyKpiWeekLabel: 'KPI week (Mon–Sun)',
  weeklyReportPreviousWeek: 'Previous week',
  weeklyReportNextWeek: 'Next week',
  weeklyReportGenerate: 'Generate report',
  weeklyReportRegenerate: 'Generate again',
  weeklyReportGenerating: 'Reading the week and writing it up. This takes up to a minute.',
  weeklyReportEmpty: 'No report has been generated for this week yet.',
  weeklyReportSavedAt: 'Generated {at}',
  weeklyReportSummaryHeading: 'Summary',
  weeklyReportWorkHeading: 'Work this week',
  weeklyReportBugsHeading: 'Bugs tracked',
  weeklyReportPlanHeading: 'Next week',
  weeklyReportMeetingsHeading: 'Meetings',
  weeklyReportWorkCounts: '{done} of {total} done · {inProgress} in progress',
  weeklyReportBugCounts: '{tracked} tracked · {closed} closed · {inProgress} in progress · {high} high',
  weeklyReportListTruncated: 'Showing {shown} of {total}. The counts above cover all of them.',
  weeklyReportNoWork: 'No ticket activity in this week.',
  weeklyReportNoBugs: 'No bug was opened or closed in this week.',
  weeklyReportNoPlan: 'Nothing is scheduled from tickets or minutes yet.',
  weeklyReportNoMeetings: 'No meeting of yours has minutes in this week.',
  weeklyReportTicketsUnavailable:
    'Backlog tickets could not be read, so the work, bugs and ticket-based plan are missing — this is not a week without work.',
  weeklyReportDegraded:
    'The written summary did not come back. Every count below is still correct; generate again for the summary.',
  weeklyReportExportSlides: 'Export slides',
  weeklyReportExporting: 'Building the deck…',
  weeklyPlanSourceDueDate: 'Due date',
  weeklyPlanSourceMilestone: 'Milestone',
  weeklyPlanSourceMeeting: 'From a meeting',
  weeklyKpiHeading: 'KPI form',
  weeklyKpiDraft: 'Draft with AI',
  weeklyKpiDrafting: 'Drafting…',
  weeklyKpiSave: 'Save KPI',
  weeklyKpiSaving: 'Saving…',
  weeklyKpiEmpty: 'No KPI form saved for this week yet.',
  weeklyKpiEdited: 'Saved with your edits',
  weeklyKpiOverallStatus: 'Overall status',
  weeklyKpiAchievement: 'This week achievement',
  weeklyKpiIssues: 'Issues',
  weeklyKpiRisk: 'Risk',
  weeklyKpiSupport: 'Support / decisions needed',
  weeklyKpiHealth: 'Health',
  weeklyKpiFollowUp: 'Follow-up status',
  weeklyKpiNone: 'None',
  weeklyKpiNotAssessed: 'Not assessed — tickets could not be read',
  failedGenerateWeeklyReport: 'The weekly report could not be generated.',
  failedLoadWeeklyReport: 'The weekly report could not be loaded.',
  failedGenerateWeeklyKpi: 'The KPI draft could not be generated.',
  failedSaveWeeklyKpi: 'The KPI form could not be saved.',
  failedExportWeeklySlides: 'The slide deck could not be built.',
  projectLabel: 'Project',
  projectPlaceholder: 'No project',
  projectOther: 'Other',
  projectLoading: 'Loading projects…',
  projectNone: 'No projects available',
  projectNoneHint: 'The Backlog space has no active project to choose from.',
  projectLoadFailed: 'Projects unavailable',
  failedLoadProjects: 'Could not load projects.',
  failedLoadProjectContext: 'Could not load project context.',
  failedSaveProjectContext: 'Could not save project context.',
  failedSaveProjectAiVersion: 'Could not save the AI version.',
  failedLoadProjectContextVersions: 'Could not load context versions.',
  failedRestoreProjectContextVersion: 'Could not restore that context version.',
  // Shared-room transcript history (BRAI-539)
  failedLoadRoomHistory: 'Could not load what happened before you joined.',
  failedSaveRoomHistory: 'Could not save what this room just said.',
  projectSearch: 'Search projects',
  projectSearchEmpty: 'No project matches that search',
  // The list is per-reader now: it is whatever their own API key can see.
  projectConnectRequired: 'Connect Backlog to choose a project',
  projectReauthRequired: 'Backlog no longer accepts your key',
  projectConnectHint: 'The project list is whatever your own Backlog account can see.',
  projectOpenSettings: 'Open settings',
  projectManagement: 'Project Management',
  projectsHeading: 'My projects',
  projectsAccessSummary: '{count} projects you have access to',
  projectsAccessSummaryOne: '1 project you have access to',
  projectsDirectorySearch: 'Search by project name or key',
  projectsCreate: 'Create project',
  projectsMeetingCount: '{count} meetings',
  projectsMeetingCountOne: '1 meeting',
  projectsDetails: 'Details',
  projectsOpenDetail: 'Open {name}',
  projectsRange: 'Showing {start} to {end} of {total} projects',
  projectDetailBack: 'Back to projects',
  projectDetailOverview: 'Overview',
  projectDetailMembers: 'Members',
  projectDetailMeetings: 'Meetings',
  projectDetailName: 'Project name',
  projectDetailKey: 'Project key',
  projectDetailMetrics: 'Project metrics',
  projectDetailTotalMeetings: 'Total: {count} meetings recorded',
  projectDetailTotalMeetingsOne: 'Total: 1 meeting recorded',
  projectDetailLastMeeting: 'Last meeting: {when}',
  projectDetailNoMeetings: 'No meetings recorded yet',
  projectDetailOpenInBacklog: 'Open in Backlog',
  projectDetailNotFound: 'No such project, or your Backlog account cannot see it.',
  projectDetailMembersFailed: 'The member list could not be loaded.',
  projectDetailNoMembers: 'This project has no members.',
  projectDetailMemberYou: 'You',
  projectDetailMemberRoleAdmin: 'Administrator',
  projectDetailMemberRoleMember: 'Member',
  projectDetailMembersRange: 'Showing {start} to {end} of {total} members',
  projectDetailMemberEmailMissing: '—',
  projectDetailMeetingsEmpty: 'No meetings on this project yet.',
  projectDetailMeetingsHeading: 'Project Meetings',
  projectDetailMeetingsSearchPlaceholder: 'Search meeting, topic',
  projectDetailMeetingsRange: 'Showing {start} to {end} of {total} meetings',
  projectDetailContextTab: 'Context & Dictionary',
  projectDetailStatusRunning: 'Running',
  projectDetailContextSubtab: 'Project context',
  projectDetailDictionarySubtab: 'Project dictionary',
  projectDetailContextTitle: 'Project context',
  projectDetailContextHint:
    'Used automatically for translation assist and terminology in every meeting on this project.',
  projectDetailContextAiVersionLabel: 'AI version',
  projectDetailContextEditAiVersion: 'Edit AI version',
  projectDetailContextSave: 'Save',
  projectDetailContextCancel: 'Cancel',
  projectDetailContextEmpty:
    'No AI version yet. Edit to add one so meetings have project background.',
  projectDetailContextInUse: 'Using the current version',
  projectDetailContextUpdatedAt: 'updated',
  projectDetailContextLatestHint: 'Automatically uses the latest version',
  projectDetailContextLatestFrom: 'from',
  projectDetailContextLatestBadge: 'Latest',
  projectDetailVersionsTitle: 'Context versions',
  projectDetailVersionsHint:
    'Each meeting or edit that changes the AI version files a new one. You can review a change or restore an earlier version.',
  projectDetailVersionsEmpty:
    'No versions yet. A version is filed after each change to the AI version.',
  projectDetailVersionsColVersion: 'Version',
  projectDetailVersionsColSource: 'Source',
  projectDetailVersionsColDate: 'Date',
  projectDetailVersionsColChanges: 'What changed',
  projectDetailVersionsColStatus: 'Status',
  projectDetailVersionsColActions: 'Actions',
  projectDetailVersionsActive: 'In use',
  projectDetailVersionsReplaced: 'Replaced',
  projectDetailVersionsView: 'View',
  projectDetailVersionsRestore: 'Restore',
  projectDetailVersionModalTitle: 'Version detail',
  projectDetailVersionCloseDialog: 'Close version detail',
  projectDetailVersionCreatedFrom: 'Created from',
  projectDetailVersionVs: 'vs',
  projectDetailVersionChanges: 'changes',
  projectDetailVersionAddedLegend: 'New or changed information',
  projectDetailVersionRemovedLegend: 'Removed information',
  projectDetailVersionRestore: 'Restore this version',
  projectDetailVersionAiProfileLabel: 'AI version',
  projectDetailVersionSourceHuman: 'Edited by a person',
  projectDetailVersionSourceManual: 'Manual edit',
  projectDetailVersionSourceRestore: 'Restored version',
  projectDetailVersionUnknownMeeting: 'Meeting',
  projectDetailDictionaryEmpty: 'No dictionary terms yet.',
  projectDetailDictSearchPlaceholder: 'Search terms...',
  projectDetailDictSourceAll: 'All sources',
  projectDetailDictSourceManual: 'Manual',
  projectDetailDictSourceAi: 'AI suggested',
  projectDetailDictImportCsv: 'Import CSV',
  projectDetailDictImportEmpty: 'No term pairs found in that file.',
  projectDetailDictReviewTitle: 'Review AI-suggested terms',
  projectDetailDictReviewEmpty:
    'Nothing to review yet. AI suggestions appear here after a meeting.',
  projectDetailDictReviewAdd: 'Add to dictionary',
  projectDetailDictReviewSkip: 'Skip',
  projectDetailDictReviewUnblock: 'Unblock',
  projectDetailDictReviewMeetings: 'meetings',
  projectDetailDictReviewLastSeen: 'last seen',
  projectDetailDictReviewAppearedSummary: 'Seen in {count} meetings · last seen {lastSeen}',
  projectDetailDictReviewAppearedCountOnly: 'Seen in {count} meeting(s)',
  projectDetailDictReviewConfidenceHigh: 'High confidence',
  projectDetailDictReviewConfidenceMedium: 'Medium confidence',
  projectDetailDictReviewConfidenceLow: 'Low confidence',
  projectDetailDictAddTerm: 'Add term',
  projectDetailDictAddTermsTitle: 'Add terms',
  projectDetailDictAddRow: 'Add row',
  projectDetailDictRemoveRow: 'Remove row',
  projectDetailDictCloseModal: 'Close',
  projectDetailDictEditTermTitle: 'Edit term',
  projectDetailDictEditTermHint: 'Changes apply to subsequent meetings on this project.',
  projectDetailDictDeleteTitle: 'Delete this term?',
  projectDetailDictDeleteTermHeading: 'Term',
  projectDetailDictDeleteBody:
    'The term will be removed from the project dictionary. AI will not use it for translation in upcoming meetings. This cannot be undone.',
  projectDetailDictColTerm: 'Term (JA)',
  projectDetailDictColTarget: 'Term (VI)',
  projectDetailDictColTargetLang: 'Lang',
  projectDetailDictColSource: 'Source',
  projectDetailDictColStatus: 'Status',
  projectDetailDictColActions: 'Actions',
  projectDetailDictEditTerm: 'Edit term',
  projectDetailDictDeleteTerm: 'Delete term',
  projectDetailDictNoMatches: 'No terms match this filter.',
  projectDetailDictLearnedTitle: 'AI-learned terms',
  projectDetailDictBlock: 'Block',
  projectDetailDictUnblock: 'Unblock',
  projectDetailDictStatusUsed: 'In use',
  projectDetailDictStatusPending: 'Needs more meetings',
  projectDetailDictStatusExpired: 'Expired',
  projectDetailDictStatusBlocked: 'Blocked',
  projectDetailDictStatusDeclared: 'Already declared',
  projectDetailDictStatusOverCap: 'Over limit',
  projectDetailDictStatusInvalid: 'Invalid',
  projectDetailDictStatusSuggested: 'AI suggestion',
  projectDetailDictTermSource: 'Term (source)',
  projectDetailDictTermTarget: 'Term (translation)',
  projectDetailDictTermLang: 'Target language',
  settingsBacklogDescription: 'The project picker and every Backlog sync run under this key.',
  glossaryLoadingDictionaries: 'Loading dictionaries...',
  glossaryLoadDictionariesFailed: 'Could not load dictionaries.',
  glossaryNoDictionaries: 'No user dictionaries available.',
  glossaryRetry: 'Retry',
  glossaryConflictWarning:
    'Found {count} conflicting terms in the selected dictionaries. Higher-priority dictionaries will be used.',
  glossaryApplyFailed:
    'Could not apply the selected dictionaries. Check your selection and try again.',
  translationHard: 'Translate now',
  translationHardHint: 'Space',
  translationHardSent: 'Sent to the AI.',
  translationHardFailed: 'No live session — nothing was sent.',

  // Per-meeting context, typed into the create-meeting form.
  contextPlaceholder:
    'e.g. Product names, attendee roles, technical terms (Japanese ↔ Vietnamese)…',

  // History
  meetingHistory: 'Meeting History',
  meetingTrash: 'Delete Meetings',
  historyBreadcrumb: 'History',
  historySubtitle: 'Manage and review all translations and notes from previous sessions.',
  trashSubtitle:
    'View meetings that have been deleted. You can restore them or permanently delete them',
  activeTab: 'Active',
  deletedTab: 'Deleted',
  searchPlaceholder: 'Search by keyword...',
  filterByDate: 'Filter by date',
  datePlaceholder: 'dd/mm/yyyy',
  filterByDirection: 'Filter by output language',
  allDirections: 'All output languages',
  directionToJapanese: 'To Japanese',
  directionToEnglish: 'To English',
  directionToVietnamese: 'To Vietnamese',
  advancedFilters: 'Advanced Filters',
  meetingNameCol: 'Meeting Name',
  dateTimeCol: 'Meeting date',
  deletedDateCol: 'Deleted date',
  durationCol: 'Duration',
  directionCol: 'Direction',
  remainingCol: 'Remaining',
  statusCol: 'Status',
  actionCol: 'Action',
  deletedStatus: 'Deleted',
  notAvailable: 'Not available',
  noDeletedMeetings: 'No deleted meetings.',
  noMeetingsFound: 'No meetings found.',
  emptyTrash: 'Trash is empty',
  select: 'Select',
  selectAll: 'Select all',
  selectMeetingAria: 'Select {title}',
  deleteSelected: 'Delete selected',
  deleteAllPermanently: 'Delete all permanently',
  remainingDays: '{count} days left',
  restore: 'Restore',
  permanentDelete: 'Delete permanently',
  // BRAI-413 — a session the user never ended. The badge sits in the History
  // row; the two lines below are the banner on the meeting's own screen.
  meetingUnfinishedBadge: 'Unfinished',
  meetingUnfinishedTitle: 'This meeting was not ended properly',
  meetingUnfinishedBody:
    'This meeting ended without being closed properly, so no transcript or minutes were saved.',
  showingRangeEmpty: '0 meetings',
  showingRange: 'Showing {start}-{end} of {total} meetings',
  previousPage: 'Previous page',
  nextPage: 'Next page',
  pageN: 'Page {n}',
  failedDeleteMeeting: 'Failed to delete meeting',
  failedRestoreMeeting: 'Failed to restore meeting',
  failedPermanentDeleteMeeting: 'Failed to permanently delete meeting',
  failedPermanentDeleteMeetings: 'Failed to permanently delete meetings',
  deleteMeetingTitle: 'Delete meeting?',
  deleteMeetingBody:
    '{title} will be moved to deleted meetings. You can review it later in the deleted list.',
  closeDeleteMeetingModal: 'Close delete meeting modal',
  deleteSelectedMeetingsTitle: 'Delete {count} meetings?',
  deleteSelectedMeetingsBody:
    '{count} meetings will be moved to deleted meetings. You can review them later in the deleted list.',
  permanentDeleteMeetingTitle: 'Permanently delete meeting?',
  permanentDeleteMeetingBody: '{title} will be permanently deleted. This action cannot be undone.',
  permanentDeleteSelectedMeetingsTitle: 'Permanently delete {count} meetings?',
  permanentDeleteSelectedMeetingsBody:
    '{count} meetings will be permanently deleted. This action cannot be undone.',

  // Meeting chat
  selectMeeting: 'Select a meeting to start',
  aiTranslating: 'BrSE AI is translating',
  readyToTranslate: 'Ready to translate',
  finishingTranslations: 'Sharing ended — finishing the last translations…',
  statusConnecting: 'Connecting...',
  statusWaitingConsent: 'Waiting for consent to join',
  statusStopping: 'Stopping...',
  statusInterrupted: 'Connection lost — recording paused',
  statusEnded: 'Meeting ended',
  statusFailed: 'Session failed',
  translationLanguageAria: 'Translation language',
  failedStartMeeting: 'Failed to start meeting',
  mute: 'Mute',
  unmute: 'Unmute',
  pause: 'Pause',
  resume: 'Resume',
  starting: 'Starting...',
  startMeetingTitle: 'Start meeting',
  startMeetingHint: 'Press Start to record and transcribe this meeting',
  copilotActionHint: 'Ask Copilot about anything said so far',
  copilotNewItemsHint: 'Copilot has new suggestions',
  aiLivePaused: 'AI Paused',
  aiLiveTranslating: 'AI Translating',
  aiLiveReady: 'AI Ready',
  recordingStatus: 'Recording',
  keyPoints: 'Key points',
  hideKeyPoints: 'Hide key points',
  showKeyPoints: 'Show key points',
  endMeeting: 'End Meeting',
  languageSwitchFailed: 'Could not switch translation language. Reverted {language}.',
  languageSwitchTimeout: 'The server did not confirm language switch. Please try again.',
  finalizingMeeting: 'Generating meeting minutes. Please wait...',
  leaveWhileRecordingPrompt:
    'Recording is in progress. If you leave, your meeting may not be saved. Continue?',
  unsavedMinutesChangesPrompt:
    'You have unsaved changes to the meeting minutes. If you leave, they will be lost. Continue?',
  failedSaveMeeting: 'Failed to save meeting',
  meetingSavedToast: 'Meeting saved to history.',
  meetingSaveFailedToast: 'Failed to save meeting. Please try again.',
  meetingMinutesUnavailable:
    'Meeting minutes are not available yet. Retry to generate them.',
  meetingMinutesNoSpeech:
    'No speech was detected in the recording, so no meeting minutes could be created.',
  meetingProjectMoveOpen: 'Change project',
  meetingProjectMoveBusyTooltip:
    'The meeting minutes are being generated. You can change the project once they are done.',
  meetingProjectMoveConfirm:
    'Move this meeting from {from} to {to}? {from} will forget what it learned from this meeting, and {to} will learn it instead.',
  meetingProjectMoveTicketsNote:
    'Backlog issues already created in {from} stay in {from}. They are not moved or re-created.',
  meetingProjectMoveRegenerateNote:
    'Regenerating replaces the meeting minutes you have now, including anything you edited by hand.',
  meetingProjectMoveAndRegenerate: 'Move and regenerate minutes',
  meetingProjectMoveOnly: 'Move only',
  meetingProjectMove: 'Move',
  meetingProjectMoveFailed: 'The meeting could not be moved to that project.',
  offlineWarningTitle: 'No internet connection',
  offlineWarningBody:
    'Recording continues locally. Please wait for the connection to be restored before ending the meeting.',
  offlineEndDisabledTooltip:
    'You are offline. Reconnect to the internet before ending the meeting.',
  offlineRestoredToast: 'Back online. You can now end and save the meeting.',
  offlineCannotEndTitle: 'Cannot save while offline',
  offlineCannotEndBody:
    'Saving requires an internet connection. Your transcript and translation are kept locally until you reconnect.',

  // Meeting detail
  meetingNotFound: 'Meeting not found',
  loadingMeeting: 'Loading meeting...',
  backToMeetings: 'Back to meetings',
  contentSummary: 'Content Summary',
  noSummaryAvailable: 'No summary available for this meeting yet.',
  noTranslationSummaryAvailable: 'No translation summary available yet.',
  clickToEdit: 'Click to edit',
  actionItems: 'Action Items',
  bilingualTranscript: 'Bilingual Transcript',
  noTranslationYet: 'No translation yet',
  noTranscriptCaptured: 'No transcript was captured for this meeting.',
  time: 'Time',
  speaker: 'Speaker',
  showLessTranscript: 'Show less transcript ↑',
  viewFullTranscript: 'View full transcript →',
  duration: 'Duration',
  translated: 'Translated',
  exportDocx: 'Export DOCX',
  sendEmail: 'Share minutes via email',
  shareMinutesEmailDescription:
    'Sends the meeting minutes for {meetingName} as an email attachment.',
  recipientEmail: 'Recipient email',
  recipientEmails: 'Recipient emails',
  recipientEmailPlaceholder: 'name@company.com',
  send: 'Send',
  sendAnyway: 'Send anyway',
  shareMinutesExternalDomainWarning:
    "This email is outside your company domain. Are you sure you want to send this meeting's minutes to it?",
  shareMinutesExternalDomainWarningOne:
    "1 email is outside your company domain. Are you sure you want to send this meeting's minutes to them?",
  shareMinutesExternalDomainWarningMany:
    "{count} emails are outside your company domain. Are you sure you want to send this meeting's minutes to them?",
  failedShareMinutesEmail: 'Failed to send meeting minutes email.',
  sendingEmail: 'Sending...',
  emailSentTitle: 'Email sent',
  emailSentBody: 'Meeting minutes for {meetingName} sent to {email}.',
  emailSentBodyMulti: 'Meeting minutes for {meetingName} sent to {emails}.',
  done: 'Done',
  automaticallySavedToHistory: 'Automatically saved to history.',
  meetingRecording: 'Meeting Recording',
  audioPlayback: 'Audio Playback',
  audioSeekAria: 'Seek audio playback',
  skipBackAria: 'Skip back 10 seconds',
  skipForwardAria: 'Skip forward 10 seconds',
  playbackSpeedAria: 'Change playback speed',
  audioUnsupported: 'Your browser does not support audio playback.',
  audioUnavailableTitle: 'Audio unavailable',
  audioUnavailableHint: "The recorded audio couldn't be saved for this meeting.",
  audioPlaybackError: 'This audio could not be loaded for playback.',
  yourFeedback: 'Your Feedback',
  translationQualityScore: 'Translation quality: {rating}/5',
  noFeedbackProvided: 'No feedback provided.',
  addFeedbackAria: 'Add feedback',
  yourRealtimeContext: 'Your Meeting Context',

  // Meeting minutes card
  meetingMinutes: 'Meeting Minutes',
  meetingInfoSection: 'Meeting info',
  purposeSection: 'Purpose',
  discussionTopicsSection: 'Discussion topics',
  actionItemsSection: 'Action items',
  meetingInfoName: 'Meeting name',
  meetingInfoDateTime: 'Date / time',
  meetingInfoParticipants: 'Participants',
  meetingInfoParticipantsUnavailable: '—',
  meetingInfoTranslationDirection: 'Translation direction',
  meetingInfoSttProvider: 'STT provider',
  meetingInfoCreatedBy: 'Created by',
  meetingInfoCreatedByValue: 'BrSE.ai (AI-generated)',
  meetingInfoCreatedDate: 'Created date',
  purposeUnavailable: 'No purpose was identified for this meeting.',
  removeTopicAria: 'Remove discussion topic',
  topicsUnavailable: 'No discussion topics were identified for this meeting.',
  topicOverviewLabel: 'Overview',
  topicDiscussionsLabel: 'Discussions',
  topicConclusionsLabel: 'Conclusions',
  topicTitlePlaceholder: 'Topic title',
  actionItemsTableNo: 'No',
  actionItemsTableContent: 'Content',
  actionItemsTableAssignee: 'Assignee',
  actionItemsTableDeadline: 'Deadline',
  actionItemsTableIssueType: 'Issue type',
  backlogIssueTypeDefault: 'Project default',
  assigneePlaceholder: 'Select assignee',
  assigneeSearch: 'Search by name or address',
  assigneeTenantGroup: 'Colleagues',
  assigneeEnterManually: 'Someone else…',
  assigneeBackToList: 'Back to list',
  assigneeNoResults: 'No members found',
  assigneeLoading: 'Loading members…',
  assigneeRetry: 'Retry',
  membersLoadError: 'Could not load members.',
  addActionItemAria: 'Add action item',
  removeActionItemAria: 'Remove action item',
  showAllActionItems: 'Show all {count} action items',

  // Meeting detail — one Edit for the whole page
  editingMeetingTitle: 'Editing - {title}',
  unsavedChangeOne: '1 unsaved change',
  unsavedChangesMany: '{count} unsaved changes',
  addPurpose: 'Add purpose',
  addDiscussion: 'Add discussion',
  addConclusion: 'Add conclusion',
  addTopic: 'Add topic',
  addAction: 'Add action',
  addShort: 'Add',
  discussionsUnavailable: 'No discussions yet.',
  conclusionsUnavailable: 'No conclusions yet.',
  actionItemsEmptyHint: 'No actions yet. Click + Add action to add one.',
  actionItemsEmptyShort: 'No actions yet.',
  removePurposeAria: 'Remove purpose',
  removeDiscussionAria: 'Remove discussion',
  removeConclusionAria: 'Remove conclusion',
  collapseTopicAria: 'Collapse topic',
  expandTopicAria: 'Expand topic',
  purposePlaceholder: 'Enter the meeting purpose...',
  topicOverviewPlaceholder: 'Enter an overview for this topic...',

  // Backlog sync (BRAI-464)
  nextActionsSection: 'Next actions',
  nextActionsNeedsProject:
    'Pick a Backlog project to list action items and push them as issues.',
  backlogColType: 'Type',
  backlogTypeNew: 'New ticket',
  backlogTypeUpdate: 'Update {key}',
  backlogChecking: 'Checking Backlog…',
  backlogSync: 'Sync to Backlog',
  backlogSyncing: 'Syncing…',
  backlogEditModeHint: 'Save the minutes first — the sync reads the saved version.',
  backlogDryRunNotice: 'The server is in dry-run mode — nothing will be written to Backlog.',
  backlogColAction: 'Action',
  backlogColTicket: 'Ticket',
  backlogColStatus: 'Status',
  backlogMatchedHint:
    'Matches an existing ticket. Overwriting it is not available yet — update it on Backlog for now.',
  backlogSummary: 'Created {created} · Updated {updated} · Skipped {skipped} · Failed {failed}',

  // The reviewed push (BRAI-575)
  backlogPush: 'Push to Backlog',
  backlogColBacklog: 'Backlog',
  backlogActionCreate: 'Create new',
  backlogActionUpdate: 'Update',
  backlogRowExcluded: 'Not going to Backlog',
  backlogRowNotPushed: 'Not pushed',
  backlogRowSkipped: 'Skipped',
  backlogRowDuplicate: 'Already created',
  backlogRowFailed: 'Failed',
  backlogRowIncludeAria: 'Push action item {number} to Backlog',
  actionItemsNoDeadline: 'Not set',

  // The confirmed overwrite (BRAI-575)
  backlogRowNeedsConfirm: 'Needs confirming',
  backlogNothingToCreate:
    'Nothing to create. Updates are written from their own panel, one at a time.',
  backlogRowUpdated: 'Updated',
  backlogActionCreated: 'Created',
  backlogConfirmTitle: 'Confirm before updating {key}',
  backlogConfirmMatchHigh: 'Close match',
  backlogConfirmMatchLow: 'Loose match',
  backlogConfirmMatchHelp:
    'How sure the server is that this action item is the same work as {key}. Check the fields below before agreeing to overwrite it.',
  backlogConfirmColField: 'Field',
  backlogConfirmColCurrent: 'Now',
  backlogConfirmColNext: 'After updating',
  backlogFieldSummary: 'Title',
  backlogFieldPriority: 'Priority',
  backlogFieldAssignee: 'Assignee',
  backlogFieldDueDate: 'Due date',
  backlogFieldDescription: 'Description',
  backlogConfirmDescriptionEmpty: 'This ticket has no description yet.',
  backlogConfirmShowAll: 'Show all',
  backlogConfirmShowLess: 'Show less',
  backlogConfirmDescriptionUnreadable: 'The current description could not be read.',
  backlogConfirmStale: 'Read from a cached copy — open the ticket if anything looks wrong.',
  backlogConfirmBlocked: 'Nothing was written. Check the ticket below and try again.',
  backlogConfirmNoop: 'Nothing to change — the ticket already matches.',
  backlogConfirmMatchReason: 'Why this ticket: {reason}',
  backlogConfirmEditHint: 'Edit the "After updating" column if anything is wrong.',
  backlogConfirmReset: 'Reset',
  backlogConfirmApply: 'Confirm update',
  backlogConfirmSkip: 'Skip this one',
  backlogConfirmApplying: 'Updating…',
  backlogConfirmUnassigned: 'Nobody',
  backlogConfirmKeepDueDate: 'Unchanged',
  backlogSummaryBar:
    'Created {created} · Updated {updated} · Needs confirming {confirm} · Already created {duplicate} · Skipped {skipped} · Failed {failed}',
  backlogSummaryRemaining: 'Confirm #{number} to finish',
  backlogSummaryDone: 'Everything selected has been written to Backlog.',
  backlogViewIssue: 'Open ticket',
  backlogDismissResults: 'Dismiss',
  backlogMembersUnavailable:
    'Could not load Backlog members — the sync will use the name from the minutes.',
  failedSyncBacklog: 'Could not sync to Backlog.',

  // Backlog connection (BRAI-537)
  backlogConnectionTitle: 'Backlog account',
  backlogConnectionConnected: 'Connected as {name}',
  backlogConnectionNone: 'Your Backlog account is not connected.',
  backlogConnectionExpired: '{space} no longer accepts your connection.',
  backlogConnectionSpaceUnknown: 'The Backlog space cannot be reached right now.',
  backlogConnectWithBacklog: 'Connect with Backlog',
  backlogSsoHint:
    'You will be sent to Backlog to approve this application, then brought back here.',
  backlogConnecting: 'Checking…',
  backlogFinishingConnection: 'Finishing the Backlog connection…',
  backlogConnectionRequiredNotice:
    'Connecting lets the project picker show the projects you belong to.',
  backlogSyncNeedsConnection: 'Connect your Backlog account to sync.',
  backlogNudgeTitle: 'A new Backlog feature',
  backlogNudgeBody:
    'This uses your Backlog account. Connect it if you need to pick a project and send action items as issues. If you do not need it, you can skip this notice.',
  backlogNudgeAction: 'Connect',
  backlogNudgeSkip: 'Skip',
  backlogNudgeDismiss: 'Dismiss',
  backlogNudgeSidebar: 'Backlog account not connected',
  failedBacklogConnection: 'Could not read your Backlog connection.',
  failedBacklogConnect: 'Could not complete the Backlog connection.',
  showLessActionItems: 'Show less',

  // Meeting insights / renderer
  input: 'Input',
  output: 'Output',
  panelTitleSeparator: ' · ',
  realTimeKeyPoints: 'Key Points & Actions',
  keyPointsPlaceholder: 'Key points will appear while the meeting is live.',
  memoInsightsLoading: 'Generating insights…',
  memoInsightsError: 'Unable to load AI insights.',
  actionItem: 'Action Item',
  confirmAction: 'Confirm Action',
  actionItemPlaceholder: 'Confirm action will be extracted from the transcript.',
  memoActions: 'Actions',
  memoTasks: 'Tasks',
  memoActionsUnavailable: 'No actions were identified for this meeting.',
  memoTasksUnavailable: 'No tasks were identified for this meeting.',
  keyPointsUnavailable: 'No key points were identified for this meeting.',
  liveMemo: 'Live Memo',
  liveMainContent: 'Summary',
  editMainContent: 'Edit',
  saveMainContent: 'Save',
  cancelMainContentEdit: 'Cancel',
  mainContentSaveFailed: 'Could not save your edits. Please try again.',
  mainContentSaveTimeout: 'The server did not confirm your edits. Please try again.',
  liveMainContentPlaceholder: 'Live insights will appear here as the meeting progresses.',
  liveOutputBatch: 'Output (Batch)',
  bilingualTimeline: 'Enhanced mode',
  originalTranscript: 'Original',
  realtimeTranslation: 'Real-time translation',
  batchTranslation: 'High-accuracy translation',
  listeningTranscript: 'Listening',
  unknownSpeaker: 'Unknown speaker',
  // Shared room the server offers when two machines hear the same call
  sharedRoomOffer: 'Looks like you are in the same meeting as {names}. Join a shared room so you all see one transcript?',
  sharedRoomOfferUnnamed: 'Looks like you are in the same meeting as another BrSE user.',
  sharedRoomRequest:
    'Looks like you are in the same meeting as {names}. Asking to join their shared room.',
  sharedRoomRequestUnnamed:
    'Looks like you are in the same meeting as another BrSE user. Asking to join their shared room.',
  sharedRoomAwaitingApproval: 'Waiting for someone in the room to accept…',
  sharedRoomCancel: 'Cancel',
  sharedRoomAgree: 'Agree',
  sharedRoomDecline: 'No',
  sharedRoomWaiting: 'Waiting for the other person…',
  sharedRoomDeclined: 'Not joining a shared room.',
  sharedRoomFailed: 'Could not join the shared room.',
  sharedRoomJoined: 'Joined a shared room with {names}',
  sharedRoomJoinedUnnamed: 'Joined a shared room',
  sharedRoomLeave: 'Leave room',
  sharedRoomLeaveFailed: 'Could not leave the shared room.',
  sharedRoomNeverAsk: 'Never ask about joining a shared room',
  sharedRoomNeverAskHint: 'Kept in this browser, not on your account.',
  sharedRoomDetectionTitle: 'Same meeting found',
  sharedRoomDetectionBodyBefore: 'Do you want to join',
  sharedRoomDetectionBodyAfter: 'with the others to improve translation quality?',
  sharedRoomDetectionBodyNoTitle:
    'Do you want to join a shared room with the others to improve translation quality?',
  sharedRoomSimilarContentBadge: 'Similar content',
  sharedRoomDetectionMeetingNamePlaceholder: 'Meeting name',
  sharedRoomDetectionStartedMeta: 'Started {started} · {languages}',
  sharedRoomDetectionInfo:
    'In a shared room, BrSE.ai uses the clearest audio from all members for translation.',
  sharedRoomParticipantsHeading: 'Participants ({count})',
  sharedRoomMeetingNameFallback: 'Meeting',
  sharedRoomMeetingShortLabel: 'Meeting#{id}',
  sharedRoomUnnamedPeer: 'Another participant',
  similarMeetingDetected: 'Similar meeting detected',
  sharedRoomJoinLink: 'Join',
  sharedRoomHeaderJoined: 'Shared room · {title}',
  sharedRoomMembersTitle: 'Members ({count})',
  sharedRoomMemberHost: 'Host',
  sharedRoomMemberYou: 'You',
  sharedRoomMemberJoinedAt: 'Joined {time}',
  sharedRoomToastJoined: 'You joined the shared room with {count} others.',
  sharedRoomToastJoinedSolo: 'You joined the shared room.',
  sharedRoomToastContinuing: "You're continuing in your own meeting.",
  sharedRoomToastLeft: 'You left the shared room.',
  sharedRoomLeaveConfirmTitle: 'Leave the shared room?',
  sharedRoomLeaveConfirmBodyIntro: 'You will leave the shared room of',
  sharedRoomLeaveConfirmBodyOutro: 'and go back to your own meeting.',
  sharedRoomLeaveConfirmBodyNoTitle:
    'You will leave this shared room and go back to your own meeting.',
  sharedRoomLeaveConfirmInRoom: 'In this room',
  sharedRoomLeaveConfirmWarning:
    'After you leave, BrSE.ai will translate from your audio only.',
  sharedRoomSubtitleFallback:
    'Shared room · best audio from all participants',
  sharedRoomYes: 'Yes',
  sharedRoomNo: 'No',
  sharedRoomProjectToastManual: '{name} changed the room project to {project}.',
  sharedRoomProjectNoticeMajority:
    'This shared room is using project {project} (majority). Your meeting will move to {project}.',
  sharedRoomProjectNoticeTitle:
    'This shared room is using project {project} (meeting title). Your meeting will move to {project}.',
  sharedRoomProjectNoticeAccess:
    'This shared room is using project {project} (Backlog access). Your meeting will move to {project}.',
  sharedRoomProjectNoticeFirstJoined:
    'This shared room is using project {project} (first joined). Your meeting will move to {project}.',
  sharedRoomProjectNoticeManual:
    'This shared room is using project {project}. Your meeting will move to {project}.',
  sharedRoomProjectRoomLabel: 'Room · {project}',
  projectUpdateNotSupported: 'Clearing the project is not available in a shared room.',
  projectUpdateNotReadable: 'You cannot use that project in Backlog. The room project is unchanged.',
  showBilingualTimeline: 'Show enhanced mode',
  hideBilingualTimeline: 'Hide enhanced mode',
  showInput: 'Show input',
  hideInput: 'Hide input',
  showOutput: 'Show output',
  hideOutput: 'Hide output',
  showLiveMemo: 'Show live memo',
  hideLiveMemo: 'Hide live memo',
  showOutputBatch: 'Show output (batch)',
  hideOutputBatch: 'Hide output (batch)',
  realtimeContextLabel: 'Add Context',
  realtimeContextPlaceholder: 'e.g. Viewing slide 3: system architecture… (leave blank to clear)',
  realtimeContextSubmit: 'Save context',
  realtimeContextSaved: 'Context saved ({count} chars)',
  realtimeContextTooLong: 'Context must be {max} characters or fewer.',
  realtimeContextUpdateFailed: 'Failed to update context',
  realtimeContextScopePrefix: 'Applied to ',
  realtimeContextScopeSuffix: '. Real-time caption is not affected.',
  realtimeContextEmptyHint: 'Enter terms, project names, or people AI should understand correctly',
  feedbackLabel: 'Feedback',
  feedbackTooltip: 'Got an issue or feedback? Let us know!',
  feedbackHeaderCompose: 'Quick Feedback',
  feedbackHeaderList: 'My Feedback',
  feedbackPopoverPlaceholder: 'Share feedback, bug report, or any suggestions...',
  feedbackSubmit: 'Send',
  feedbackSubmitSuccess: 'Feedback sent! Thank you 🙌',
  feedbackSubmitError: 'Failed to send feedback',
  feedbackSubmitFailedRetry: 'Failed to send. Try again?',
  feedbackRetry: 'Retry',
  feedbackReadOnly: 'Read-only',
  feedbackNewFeedback: '+ New feedback',
  waitingSource: 'Waiting for source…',
  waitingTranslation: 'Waiting for translation…',
  startSpeakingTitle: 'Start speaking to see transcription',
  startSpeakingBody: 'Original speech and translations will be grouped together by speaker turn.',
  // Enhanced Mode waits on a batch pass, so it stays empty for a while after
  // the raw panes have filled. Its own copy says so; the two above are shared
  // with the Input and Output panes, where the latency note would be wrong.
  enhancedWaitingTitle: 'Listening...',
  enhancedWaitingBody:
    'Translation appears after someone speaks. Enhanced mode results can take 10–30 seconds.',
  jumpToLive: '↓ Jump to Live',
  transcriptAria: 'Transcript in two columns: source and translation',
  hideMeetingFooter: 'Hide controls',
  showMeetingFooter: 'Show controls',

  copilotLabel: 'Copilot',
  copilotTitle: 'AI Copilot',
  copilotListening: 'Listening',
  copilotSuggestionsTitle: 'Suggestions from the meeting',
  copilotFilterWeb: 'Web search',
  copilotFilterBacklog: 'Backlog',
  copilotSearchFilterLabel: 'Search category',
  copilotRefresh: 'Refresh',
  copilotAnswerHeading: 'Answer',
  copilotSearchingAnswer: 'Finding an answer…',
  copilotInputPlaceholder: 'Ask Copilot anything',
  copilotResizeLabel: 'Drag to resize the Copilot panel',
  copilotSourceWeb: 'Web',
  copilotSourceBacklog: 'Backlog',
  copilotSourceMeeting: 'Past meetings',
  copilotReferenceDetails: 'Details',
  copilotTagTechnical: 'Technical',
  copilotCloseAria: 'Close Copilot',
  copilotEmpty: 'Suggestions and answers will appear here.',
  copilotYourQuestionsTitle: 'Your questions',

  assistPanelEmpty: 'Suggestions and answers will appear here.',
  assistSources: 'Sources',
  assistAnswerPending: 'Looking into "{question}"...',
  assistAnswerFailed: 'Could not find an answer.',
  assistNeedsBacklogConnection:
    'Backlog questions need your own Backlog account. Connect it in settings, then ask again.',
  assistBacklogUnavailable: 'Backlog lookup is not switched on for this server.',
  assistBacklogNoProjects:
    'Your Backlog account is connected, but it belongs to no project synced here.',
  assistBacklogNoMeetingProject:
    'This meeting has no Backlog project. Pick one for the meeting, or name a ticket key in your question.',
  assistBacklogNotMeetingMember:
    "Your Backlog account cannot see this meeting's project. Name a ticket key, or ask to be added to the project.",
  assistBacklogProjectNotSynced:
    'This project has not been synced into Brse.AI. Pick another project, or include the ticket key in your question.',
  assistBacklogScopeFailed:
    'Could not check which Backlog projects you belong to. Please ask again.',
  assistOpenSettingsNewTab: 'Connect Backlog (opens a new tab)',

  // The Copilot panel's prerequisite strip (BRAI-689). Shown before the reader
  // asks a question that cannot be answered, rather than as a refusal after.
  copilotSetupConnectTitle: 'Connect Backlog to ask about tickets',
  copilotSetupConnectBody:
    'Copilot looks up tickets with your own Backlog account. Your meeting keeps recording while you connect.',
  copilotSetupConnectAction: 'Connect Backlog',
  copilotSetupReauthTitle: 'Reconnect Backlog',
  copilotSetupReauthBody:
    'Your Backlog connection stopped working, so ticket questions cannot be answered until it is renewed.',
  copilotSetupReauthAction: 'Reconnect Backlog',
  copilotSetupProjectWarning:
    'No Backlog project yet. Select one so Copilot can answer about tickets.',
  copilotSetupApplying: 'Applying...',
  copilotSetupApplied: 'Copilot is now using {project}.',
  copilotSetupApplyFailed: 'Could not apply that project. Please try again.',
  copilotSetupUnsupported:
    'Saved to the meeting, but this recording is still using the previous project.',
  copilotSetupConnected: 'Backlog connected. Ticket questions can be answered now.',
  copilotSetupDismiss: 'Dismiss',

  /** The Copilot model switch. The hints are the only reason to prefer one. */
  assistModelLabel: 'Copilot model',
  assistModelGemini: 'Gemini',
  assistModelGpt: 'GPT',
  assistModelGeminiHint: 'Faster',
  assistModelGptHint: 'Cheaper',
  assistModelUnavailable: 'not on this server',

  backlogConnectDialogTitle: 'Connect Backlog',
  backlogConnectDialogBody:
    'Approval opens in a separate window. This tab keeps recording, so nothing is lost.',
  backlogConnectDialogAction: 'Continue to Backlog',
  backlogConnectDialogWaiting: 'Waiting for you to approve in the Backlog window...',
  backlogConnectDialogBlocked:
    'Your browser blocked that window. Allow pop-ups for this site, or open the approval yourself.',
  backlogConnectDialogOpenManually: 'Open approval',
  backlogConnectDialogFailed: 'Could not start the Backlog approval.',
  backlogConnectDialogCancel: 'Cancel',
  backlogConnectDialogProjectTitle: 'Which project is this meeting about?',
  backlogConnectDialogProjectBody:
    'Copilot answers about this project\u2019s tickets. You can change it later from the panel.',
  backlogConnectDialogPickLater: 'Not now',
  backlogConnectDialogProjectsFailed:
    'Could not load your projects. You can pick one from the panel instead.',
  backlogConnectDialogRetrying: 'Asking your earlier questions again...',
  assistAskErrorBadRequest: 'Something went wrong with that question — please try again.',
  assistAskErrorSessionNotReady:
    'Start the meeting and wait until translation is live before asking Copilot.',
  assistAskErrorNotSupported:
    'Copilot Q&A is not enabled on the AI service for this session. Ask your team to enable the assist answer flow on the STT proxy (not only the UI flag).',
  featuresAssistUnavailable: 'The assist panel is turned off on the server.',
  featuresUpdateError: 'Could not update the live panels. Please try again.',
  assistConflictInMeeting: 'In this meeting',
  assistConflictInBacklog: 'In Backlog',
  assistTicketLink: 'View in Backlog',
  /**
   * One side of a conflict can genuinely hold nothing — a ticket with no deadline set, or a
   * meeting that named a status without naming a date. Shown rather than left blank, because a
   * blank line next to a filled one reads as a rendering fault.
   */
  conflictValueUnset: 'Not set',
  conflictAlertTitle: 'Warning: the {field} just mentioned differs from Backlog',
  conflictAlertBody:
    'The meeting just said {field} {incoming}, but ticket {issueKey} on Backlog records {current}.',
  conflictAlertDaysApart: '({days} days apart)',
  conflictAlertFieldDeadline: 'deadline',
  conflictAlertFieldStatus: 'status',
  conflictAlertViewDetail: 'View details',
  conflictAlertDismiss: 'Dismiss this warning',

  durationWarningTitle: 'Still using BrSE.ai?',
  durationWarningBody:
    "This meeting has been running for a while. If there's no response, BrSE.ai will pause translation.",
  durationWarningYesContinue: 'Yes, continue',
  durationWarningNo: 'No',
  durationWarningEndTitle: 'End this meeting?',
  durationWarningEndBody:
    "BrSE.ai will stop and take you to the Meeting Minutes screen. This can't be undone.",
  durationWarningYesStop: 'Yes, stop BrSE.ai',
  durationWarningNoGoBack: 'No, go back',

  // Feedback modal
  howWasMeeting: 'How was your meeting?',
  feedbackSubtitle: 'Your feedback helps our AI bridge the gap better next time.',
  translationQuality: 'Translation Quality',
  additionalComments: 'Additional Comments or Feedback',
  feedbackPlaceholder:
    'Was there anything specific our AI missed? Terms, cultural nuances, or tone?',
  submitFeedback: 'Submit Feedback',
  skipFeedback: 'Skip',
  rateStar: 'Rate {n} out of 5',
  durationLessThanMin: '< 1 min',

  // Auth
  login: 'Log in',
  loggingIn: 'Logging in…',
  loginSubtitle: 'Sign in with your React Plus Teams account',
  email: 'Email',
  password: 'Password',
  passwordPlaceholder: 'Enter your password',
  showPassword: 'Show password',
  hidePassword: 'Hide password',
  or: 'or',
  loginWithGoogle: 'Sign in with Google',
  loginWithMicrosoft: 'Sign in with Microsoft Teams',
  noAccount: "Don't have an account?",
  register: 'Sign up',
  invalidCredentials: 'Invalid email or password',
  emailRequired: 'Please enter your email',
  emailInvalid: 'Please enter a valid email address',
  passwordRequired: 'Please enter your password',
  registerTitle: 'Sign up',
  registerSubtitle: 'Create your React Plus Teams account',
  haveAccount: 'Already have an account?',
  registering: 'Signing up…',
  createAccount: 'Create account',
  name: 'Name',
  namePlaceholder: 'e.g. Taro Yamada',
  nameRequired: 'Please enter your name',
  nameMinLength: 'Please enter at least 2 characters for your name',
  confirmPassword: 'Confirm password',
  confirmPasswordPlaceholder: 'Enter your password again',
  confirmPasswordRequired: 'Please confirm your password',
  passwordRequirements: 'At least 8 characters with uppercase, lowercase, number, and symbol',
  passwordMinLength: 'Please enter at least 8 characters',
  passwordComplexity: 'Please include uppercase, lowercase, number, and symbol',
  passwordsMustMatch: 'Passwords do not match',
  role: 'Role',
  roleRequired: 'Please select a role',
  roleDeveloper: 'Developer',
  roleProjectManager: 'Project Manager (PM)',
  roleBrse: 'Bridge SE (BrSE)',
  registrationSuccess: 'Registration complete. Redirecting…',
  emailAlreadyRegistered: 'This email address is already registered',
  unexpectedError: 'An unexpected error occurred',
  loginInfoIncomplete: 'Login information is incomplete',
  loginFailed: 'Login failed',
  backToLogin: 'Back to login',
  processingLogin: 'Processing login…',

  // Errors
  genericError: 'Something went wrong',

  // Service-layer errors (surfaced via ApiError.message into toasts/forms)
  networkError: 'Cannot connect to the server. Please check your network.',
  authRequired: 'Authentication is required. Please log in again.',
  failedLoadMeetingList: 'Failed to load meeting list',
  failedRegister: 'Registration failed',
  failedLogin: 'Login failed',
  failedMicrosoftLogin: 'Microsoft SSO login failed',
  failedGoogleLogin: 'Google SSO login failed',
  sonioxTokenInvalid: 'Invalid Soniox token response',
  sonioxTokenFailed: 'Failed to obtain Soniox token',
  failedValidateMeetingTitle: 'Failed to validate meeting title',
  failedDeleteMeetingService: 'Failed to delete meeting',
  failedRestoreMeetingService: 'Failed to restore meeting',
  failedPermanentDeleteMeetingService: 'Failed to permanently delete meeting',
  failedPermanentDeleteMeetingsService: 'Failed to permanently delete meetings',
  failedLoadMeetingDetails: 'Failed to load meeting details',
  failedSaveMeetingService: 'Failed to save meeting',
  failedUpdateMeeting: 'Failed to update meeting',
  meetingTitleTakenError:
    'A meeting with this name already exists. Please choose a different name.',
  failedUploadMeetingAudio: 'Failed to upload meeting audio',
  failedGenerateMeetingMinutes: 'Failed to generate meeting minutes',
  failedTranslateMeetingMinutes: 'Failed to translate meeting minutes',
  meetingMinutesTimedOut: 'Timed out waiting for meeting minutes',
  failedSummarizeMeetingMemo: 'Failed to summarize meeting memo',
  failedAnalyzeMeetingFiles: 'Failed to analyze meeting files',
  failedLoadMemoInsights: 'Failed to load memo insights',
  failedStopRecording: 'Failed to stop recording',
  failedStartRecording: 'Failed to start recording',
  failedStartAudioCapture: 'Failed to start audio capture',
  sttTokenInvalidResponse: 'STT token response is missing token or wsUrl',
  websocketError: 'WebSocket error',
  sttInvalidToken: 'STT session rejected: invalid token',
  sttTokenExpired: 'STT session rejected: token expired',
  sttProviderUnavailable: 'STT provider is currently unavailable',
  sttInactivityTimeout: 'STT session closed after 30s of inactivity',
  sttProtocolViolation: 'STT session closed due to a protocol violation',
  sttInternalServerError: 'STT session closed due to an internal server error',
  audioTrackMissing: 'No audio track found — please tick "Share tab audio" in the browser prompt.',
  microphoneTrackMissing: 'No microphone track found — please allow microphone permission.',
  microphoneTrackMissingShort: 'No microphone track found.',
  microphoneAccessEnded: 'Microphone access ended.',
  screenShareStopped: 'You stopped sharing.',
  captureLostDrainingTitle: 'Sharing stopped',
  captureLostDrainingBody:
    'Recording has ended. Collecting the last translations — this can take up to a minute.',
  captureLostDoneTitle: 'Recording stopped',
  captureLostDoneBody: 'Press Start to share again and continue this meeting.',
  mediaRecorderErrorTab: 'Audio capture error during tab audio capture.',
  mediaRecorderErrorMic: 'Audio capture error during microphone capture.',
  missingTextToAnalyze: 'Missing text to analyze',
  noMessagesToSummarize: 'No messages to summarize',
  noMessagesToAnalyze: 'No messages to analyze',
  openAiErrorFmt: 'OpenAI error: {status} - {detail}',
} as const

const ja: { [K in keyof typeof en]: string } = {
  teamsCalendarAllMeetings: 'Teams の会議',
  teamsCalendarOrganizer: '主催者',
  teamsCalendarScheduledTime: '予定日時',
  teamsCalendarHint: '今後7日間の Microsoft Teams 会議です。',
  teamsCalendarConnectHint: 'Microsoft アカウントを接続すると、Teams の予定がここに表示されます。',
  teamsCalendarReconnectHint: '予定表へのアクセスが期限切れ、または拒否されました。Microsoft アカウントを再接続してください。',
  teamsCalendarConnect: 'Microsoft 予定表を接続',
  teamsCalendarReconnect: 'Microsoft 予定表を再接続',
  teamsCalendarDisconnect: '予定表の接続を解除',
  teamsCalendarRefresh: '予定表を更新',
  teamsCalendarJoin: 'Teams で参加',
  teamsCalendarLoading: 'Teams の予定を読み込み中...',
  teamsCalendarLoadFailed: 'Teams の予定を取得できません。再試行してください。',
  teamsCalendarConsentFailed: 'Microsoft 予定表に接続できません。再試行し、予定表へのアクセスを許可してください。',
  /* AI DRAFT — needs a native-speaker review before release. */
  googleCalendarAllMeetings: 'Google Meet の会議',
  /* AI DRAFT — needs a native-speaker review before release. */
  googleCalendarHint: '今後7日間の Google Meet 会議です。',
  /* AI DRAFT — needs a native-speaker review before release. */
  googleCalendarConnectHint: 'Google でサインインし、カレンダーへのアクセスを許可すると、Google Meet の予定がここに表示されます。',
  /* AI DRAFT — needs a native-speaker review before release. */
  googleCalendarReconnectHint: 'カレンダーへのアクセスが期限切れ、または拒否されました。もう一度 Google でサインインしてください。',
  /* AI DRAFT — needs a native-speaker review before release. */
  googleCalendarConnect: 'Google カレンダーを接続',
  /* AI DRAFT — needs a native-speaker review before release. */
  googleCalendarReconnect: 'Google カレンダーを再接続',
  /* AI DRAFT — needs a native-speaker review before release. */
  googleCalendarRefresh: '予定表を更新',
  /* AI DRAFT — needs a native-speaker review before release. */
  googleCalendarJoin: 'Google Meet で参加',
  /* AI DRAFT — needs a native-speaker review before release. */
  googleCalendarLoading: 'Google Meet の予定を読み込み中...',
  /* AI DRAFT — needs a native-speaker review before release. */
  googleCalendarLoadFailed: 'Google Meet の予定を取得できません。再試行してください。',
  /* AI DRAFT — needs a native-speaker review before release. */
  googleCalendarConsentFailed: 'Google カレンダーに接続できません。再試行し、カレンダーへのアクセスを許可してください。',
  /* AI DRAFT — needs a native-speaker review before release. */
  // Meeting brief (Upcoming)
  briefView: 'ブリーフを見る',
  briefButton: 'ブリーフ',
  briefCloseDialog: 'ミーティングブリーフを閉じる',
  briefModalTitle: 'ミーティングブリーフ · {meeting}',
  briefNearest: '直近',
  briefAiEmpty: 'ブリーフはまだありません · プロジェクトを選択して開始',
  briefAiGenerating: '{project} のブリーフを作成中...',
  briefAiPreparing: 'AI がカレンダー名からブリーフを準備しています…',
  briefAiGenerated: '{time} に {date} の会議と Backlog から AI が要約しました',
  briefAiFailed: 'ブリーフを作成できませんでした',
  briefProjectLabel: 'プロジェクト',
  briefProjectPlaceholder: 'プロジェクトを選択...',
  briefProjectSearch: 'プロジェクトを検索...',
  briefProjectNoResults: '一致するプロジェクトがありません。',
  briefProjectSuggested: '会議名からのおすすめ',
  briefProjectHint: 'AI はカレンダー名からプロジェクトを推定します。違う場合はここで変更してください。',
  briefRefresh: '更新',
  briefEmptyTitle: 'ブリーフはまだありません',
  briefEmptyBody:
    '上のプロジェクトを選択すると、AI が前回の会議の要約、Backlog チケットの状況、関連コメントをまとめます。',
  briefStepProject: 'プロジェクト {project} を選択しました',
  briefStepMinutes: '{date} の会議要約を読み込みました',
  briefStepTickets: 'プロジェクトの Backlog チケットを取得中...',
  briefStepSynthesize: 'ブリーフをまとめる',
  briefLoading: 'ミーティングブリーフを準備中...',
  briefAgendaTitle: 'アジェンダ',
  briefAgendaSectionProgress: '進捗',
  briefAgendaSectionDecisions: '要決定',
  briefAgendaSectionQuestions: '想定 Q&A',
  briefAgendaSectionRisks: 'リスク',
  briefPreviousMeeting: '前回の会議の要約',
  briefOpenMinutes: '議事録を開く',
  briefMinutesAccessDenied:
    '{title} は共有されていません。閲覧権限についてはオーナー {ownerName}（{ownerEmail}）にお問い合わせください。',
  briefMinutesAccessDeniedNoOwner:
    '{title} は共有されていません。閲覧権限については議事録のオーナーにお問い合わせください。',
  briefMeetingMeta: '会議 #{number} · {title} · {date} · {duration} 分',
  briefMeetingMetaPlain: '{title} · {date} · {duration} 分',
  briefPreparing: '{project} のブリーフを準備中...',
  briefAiSkipped: 'ブリーフできる議事録付きの前回会議がまだありません',
  briefNoBriefableTitle: 'ブリーフできる前回の会議がありません',
  briefNoBriefableBody:
    'このプロジェクトには、ブリーフできる議事録付きの会議がまだありません。会議ができるまでポップアップは空のままです。',
  briefAgreed: '合意事項',
  briefAssigned: '割り当てられた作業',
  briefAssignedCreated: '{count} 件 · Backlog にチケット作成済み（下記）',
  briefAssignedCreatedOne: '1 件 · Backlog にチケット作成済み（下記）',
  briefAssignedNotCreated: '{count} 件 · Backlog には未作成',
  briefAssignedNotCreatedOne: '1 件 · Backlog には未作成',
  briefBacklogTitle: 'Backlog チケットの状況',
  briefTicketCount: '前回の会議からの {count} 件',
  briefTicketCountOne: '前回の会議からの 1 件',
  briefBacklogBreakdown: 'Resolved {resolved} · In Progress {progress} · Open {open}',
  briefDueToday: '本日期限',
  briefDueInDays: '期限 {date} · あと {count} 日',
  briefDueTomorrow: '期限 {date} · あと 1 日',
  briefDueOverdue: '期限 {date} · {count} 日超過',
  briefDueOverdueOne: '期限 {date} · 1 日超過',
  briefDoneOn: '{date} 完了',
  briefCommentsTitle: '関連コメントの要約',
  briefCommentsHint: '前回の会議以降、上記チケットに付いた新しいコメント',
  briefNoComments: '新しいコメントはありません',
  briefCommentCount: '{count} 件のコメント',
  briefCommentCountOne: '1 件のコメント',
  briefCommentMeta: '{comments} · {people} · {when}',
  briefNoTickets: 'このプロジェクトに紐づく Backlog チケットはまだありません。',
  briefErrorBody: 'ブリーフを作成できませんでした。接続を確認して再試行してください。',
  briefRetry: '再試行',
  briefStartsInMinutes: '{count} 分後に開始',
  briefStartsInHour: '1 時間後に開始',
  briefStartsInHours: '{hours} 時間後に開始',
  briefStartsInHourMinutes: '1 時間 {minutes} 分後に開始',
  briefStartsInHoursMinutes: '{hours} 時間 {minutes} 分後に開始',
  briefStartsInDay: '1 日後に開始',
  briefStartsInDays: '{count} 日後に開始',
  briefStartingNow: 'まもなく開始',
  briefToday: '今日',
  briefYesterday: '昨日',
  briefDaysAgo: '{count} 日前',
  edit: '編集',
  cancel: 'キャンセル',
  save: '保存',
  saveChanges: '変更を保存',
  start: '開始',
  delete: '削除',
  deleting: '削除中...',
  view: '表示',
  share: '共有',
  shareMeetingTitle: '会議を共有',
  searchByNameOrEmail: '名前またはメールで検索',
  nSelected: '{count} 人選択中',
  continueAction: '続行',
  confirmShareTitle: '共有の確認',
  minutesTranscriptAndAudio: '議事録、トランスクリプト、音声',
  recipientsCanViewDisclaimer:
    '受信者はすべての内容を閲覧でき、他の人に再共有できます。',
  meetingSharedTitle: '会議を共有しました',
  meetingSharedBody: '「{meetingName}」を {names} と共有しました。',
  shareRecipientsAlreadyHaveAccess:
    '選択した受信者はすでにこの会議にアクセスできます。',
  shareRecipientNeedsAccount: 'BrSE.ai アカウントがありません',
  shareDirectoryUnavailable:
    'Microsoft テナントのディレクトリを利用できません。しばらくしてから再試行してください。',
  shareDirectoryNoMatch: '該当する人がいません。',
  currentlySharedWith: 'Currently shared with',
  removedSharesSection: 'REMOVED',
  revoke: 'Revoke',
  removeAccess: 'Remove access',
  revokeShareTitle: 'Remove {name} from this meeting?',
  revokeShareBody:
    'They — and anyone they shared this with — will lose access.',
  alreadySharedLabel: 'Already shared',
  /* AI DRAFT — needs a native-speaker review before release. */
  shareWithMembersLabel: '共有する相手',
  /* AI DRAFT — needs a native-speaker review before release. */
  shareWithMembersHint:
    '招待した人はこの会議にライブで参加し、共有された翻訳を見ることができます。',
  /* AI DRAFT — needs a native-speaker review before release. */
  shareWithMembersSearch: '名前またはメールでメンバーを検索',
  /* AI DRAFT — needs a native-speaker review before release. */
  shareWithMembersPlaceholder: 'まだ誰も選択していません',
  /* AI DRAFT — needs a native-speaker review before release. */
  shareWithMembersSummary: '{names} 他{count}名',
  /* AI DRAFT — needs a native-speaker review before release. */
  shareWithMembersOpen: '共有する相手を選択',
  /* AI DRAFT — needs a native-speaker review before release. */
  shareWithMembersClear: 'すべて解除',
  /* AI DRAFT — needs a native-speaker review before release. */
  shareWithMembersEmpty: '該当する人がいません。',
  /* AI DRAFT — needs a native-speaker review before release. */
  shareOnCreateFailed:
    '会議は開始しましたが、共有に失敗しました。履歴から共有できます。',
  /* AI DRAFT — needs a native-speaker review before release. */
  liveInviteButton: '招待',
  /* AI DRAFT — needs a native-speaker review before release. */
  liveInviteTitle: 'メンバーを招待',
  /* AI DRAFT — needs a native-speaker review before release. */
  liveInviteHint:
    '「{meeting}」に招待して、一緒に参加すると翻訳品質が向上します。',
  /* AI DRAFT — needs a native-speaker review before release. */
  liveInviteSubmit: '招待を送信',
  /* AI DRAFT — needs a native-speaker review before release. */
  liveInviteSending: '送信中…',
  /* AI DRAFT — needs a native-speaker review before release. */
  liveInviteClose: '招待ダイアログを閉じる',
  /* AI DRAFT — needs a native-speaker review before release. */
  liveInviteSent: '{count}人に招待を送信しました。',
  /* AI DRAFT — needs a native-speaker review before release. */
  liveInviteInThisRoom: 'このルームに参加中',
  /* AI DRAFT — needs a native-speaker review before release. */
  liveInviteWaiting: '待機中',
  /* AI DRAFT — needs a native-speaker review before release. */
  liveInviteInvitedHeading: '招待済み ({count})',
  /* AI DRAFT — needs a native-speaker review before release. */
  meetingInviteBody: '{name} さんが「{meetingName}」に招待しました。',
  /* AI DRAFT — needs a native-speaker review before release. */
  meetingInviteTitle: '会議への招待',
  /* AI DRAFT — needs a native-speaker review before release. */
  meetingInviteModalAfterName: ' さんが「',
  /* AI DRAFT — needs a native-speaker review before release. */
  meetingInviteModalAfterMeeting:
    '」に参加するよう招待しました。他の参加者と共有ルームに参加して、翻訳品質を向上させますか？',
  /* AI DRAFT — needs a native-speaker review before release. */
  meetingInviteYes: 'はい',
  /* AI DRAFT — needs a native-speaker review before release. */
  meetingInviteNo: 'いいえ',
  /* AI DRAFT — needs a native-speaker review before release. */
  meetingInviteJoin: '参加',
  /* AI DRAFT — needs a native-speaker review before release. */
  meetingInviteChecking: '確認中…',
  /* AI DRAFT — needs a native-speaker review before release. */
  meetingInviteEnded: 'その会議はすでに終了しています。',
  /* AI DRAFT — needs a native-speaker review before release. */
  meetingInviteDismiss: '閉じる',
  /* AI DRAFT — needs a native-speaker review before release. */
  joiningSharedMeetingTitle: '共有された会議に参加',
  /* AI DRAFT — needs a native-speaker review before release. */
  joiningSharedMeetingHint:
    '共有された会議に参加します。全員が同じ通話を聞けるよう、このタブの音声を共有してください。翻訳は一度だけ行われ、ルーム全体で共有されます。',
  accessRemoved: 'Access removed',
  removeFromHistory: 'Remove',
  removeFromHistoryTitle: 'Remove this meeting from your History?',
  removeFromHistoryBody:
    "This only hides it from your list. It won't remove anyone's access.",
  shareDisabledNoContent:
    'Nothing to share yet — add transcript, minutes, or audio first.',
  shareDisabledProcessing: 'Content is still being processed.',
  shareDisabledError: 'Sharing is unavailable for this meeting.',
  failedRevokeShare: 'Failed to revoke share.',
  failedHideMeeting: 'Failed to hide meeting.',
  removedOnDate: 'Removed {date}',
  meetingSharedWithOthers: '{names} and {count} others',
  sharedWithCol: '共有先',
  sharedByCol: '共有元',
  nPeople: '{count} 人',
  onePerson: '1 人',
  back: '戻る',
  failedShareMeetingService: '会議の共有に失敗しました。',
  export: 'エクスポート',
  hide: '非表示',
  show: '表示',
  close: '閉じる',
  completed: '完了',
  mins: '分',
  min: '分',
  minutes: '分',
  minute: '分',
  loading: '読み込み中...',
  saving: '保存中...',
  required: '必須',
  fieldTooLong: '{max}文字以内で入力してください。',
  pending: '未対応',
  clear: 'クリア',
  previousMonth: '前の月',
  nextMonth: '次の月',

  newVersionAvailable: '新しいバージョンがあります。クリックして再読み込みしてください。',
  reloadNow: '再読み込み',

  logout: 'ログアウト',
  lightMode: 'ライトモード',
  darkMode: 'ダークモード',
  language: '言語',
  exportingReport: '出力中...',
  exportReportError: 'レポートの出力に失敗しました',

  // Admin management screen
  adminManagement: '管理',
  adminTabUsers: 'ユーザー',
  adminTabLoginLogs: 'ログイン履歴',
  /* AI DRAFT */ adminTabActivity: '機能の利用状況',
  adminTabMeetings: '会議',
  adminDownloadExcel: 'Excelをダウンロード',
  adminLoadError: 'データの読み込みに失敗しました',
  adminEmpty: 'データがありません',
  adminSearchUsers: 'メールまたは名前で検索',
  adminSearchLoginLogs: 'メール・名前・IP・方式で検索',
  adminSearchMeetings: 'タイトルまたはメールで検索',
  adminFilterMethod: '方式',
  adminFilterMethodAll: 'すべての方式',
  adminFilterDateFrom: '開始日',
  adminFilterDateTo: '終了日',
  adminClearFilters: 'フィルターをクリア',
  adminColEmail: 'メール',
  adminColName: '名前',
  /* AI DRAFT */ adminColFunction: '機能',
  /* AI DRAFT */ adminColUses: '利用回数',
  /* AI DRAFT */ adminColSucceeded: '成功',
  /* AI DRAFT */ adminColFailed: '失敗',
  /* AI DRAFT */ adminColDistinctUsers: '利用者数',
  /* AI DRAFT */ adminActivityPeopleButton: '{label} を使った {count} 人を表示',
  /* AI DRAFT */ adminActivityPeopleTitle: '利用者',
  /* AI DRAFT */ adminActivityFailuresButton: '{label} の失敗 {count} 件を表示',
  /* AI DRAFT */ adminActivityFailuresTitle: '失敗',
  /* AI DRAFT */ adminActivityNoMeeting: '会議なし',
  /* AI DRAFT */ adminColArea: '領域',
  /* AI DRAFT */ adminColActivity: '利用履歴',
  /* AI DRAFT */ adminViewActivity: '表示',
  /* AI DRAFT */ adminUserActivityTitle: '利用履歴',
  /* AI DRAFT */ adminColStatus: 'ステータス',
  /* AI DRAFT */ adminColMeeting: '会議',
  /* AI DRAFT */ adminColProject: 'プロジェクト',
  /* AI DRAFT */ adminColWhen: '日時',
  /* AI DRAFT */ adminStatusSuccess: '成功',
  /* AI DRAFT */ adminStatusFailure: '失敗',
  /* AI DRAFT */ adminFilterFunction: '機能',
  /* AI DRAFT */ adminFilterFunctionAll: 'すべての機能',
  adminColRole: 'ロール',
  adminColProvider: '認証方式',
  adminColCreatedAt: '作成日',
  adminColLoginCount: 'ログイン回数',
  adminColLastLogin: '最終ログイン',
  adminColMeetingCount: '会議数',
  adminColTotalMinutes: '合計時間（分）',
  adminColAvgRating: '平均評価',
  adminColMethod: '方式',
  adminColIpAddress: 'IPアドレス',
  adminColUserAgent: 'ユーザーエージェント',
  adminColLoginAt: 'ログイン日時',
  adminColTitle: 'タイトル',
  adminColMeetingId: '会議ID',
  adminColDuration: '時間（hh:mm:ss）',
  adminColRating: '評価',
  adminColComment: 'コメント',
  adminColCurrency: '通貨',
  adminColCost: 'コスト（USD）',
  adminPrevPage: '前へ',
  adminNextPage: '次へ',
  adminPageInfo: '{page} / {totalPages} ページ',
  adminTotalRows: '{total} 件',
  adminTabFeedback: 'フィードバック',
  adminSearchFeedback: '名前またはコメントで検索',
  adminTabAllowedEmails: 'ログイン許可リスト',
  adminSearchAllowedEmails: 'メールアドレスまたはメモで検索',
  allowedEmailsHelp:
    '社内ドメインのメンバーは常にログインできます。それ以外の方はこのリストに登録が必要です。ここに追加することで、お客様がOutlookやGoogleでログインできるようになります。',
  allowedEmailAddress: 'メールアドレス',
  /* AI DRAFT — unreviewed Japanese */
  allowedEmailPickPlaceholder: 'アドレスを選択…',
  /* AI DRAFT — unreviewed Japanese */
  allowedEmailSearchDirectory: '名前またはアドレスで検索',
  /* AI DRAFT — unreviewed Japanese */
  allowedEmailTenantGroup: 'テナント内',
  /* AI DRAFT — unreviewed Japanese */
  allowedEmailNoDirectoryMatch: '該当する人がいません。',
  /* AI DRAFT — unreviewed Japanese */
  allowedEmailEnterManually: 'その他のアドレス（テナント外）…',
  /* AI DRAFT — unreviewed Japanese */
  allowedEmailOtherAddress: 'テナント外のメールアドレス',
  /* AI DRAFT — unreviewed Japanese */
  allowedEmailBackToList: '一覧に戻る',
  /* AI DRAFT — unreviewed Japanese */
  allowedEmailAddressRequired: 'アドレスを選択してください。',
  /* AI DRAFT — unreviewed Japanese */
  allowedEmailDirectoryHint: 'Microsoft テナント内の {count} 件のアドレス。',
  allowedEmailNote: 'メモ',
  allowedEmailNotePlaceholder: '例: ABC株式会社 — 試用',
  allowedEmailAdd: '許可する',
  allowedEmailAdding: '追加中…',
  allowedEmailAdded: 'このアドレスでログインできるようになりました。',
  allowedEmailAddFailed: 'アドレスを許可できませんでした。もう一度お試しください。',
  allowedEmailRemove: '解除',
  allowedEmailRemoved: 'このアドレスはログインできなくなりました。',
  allowedEmailRemoveFailed: 'アドレスを解除できませんでした。もう一度お試しください。',
  allowedEmailRemoveConfirm: '{email} のログイン許可を解除しますか？',
  allowedEmailColAddedBy: '追加者',
  allowedEmailColAddedAt: '追加日',
  adminColFeedbackAt: 'フィードバック日時',

  closeSidebar: 'サイドバーを閉じる',
  hideSidebar: 'サイドバーを隠す',
  showSidebar: 'サイドバーを表示',
  home: 'ホーム',
  history: '履歴',
  settings: '設定',
  /* AI DRAFT */ settingsGroupGeneral: '一般',
  /* AI DRAFT */ settingsGroupIntegrations: '連携',
  /* AI DRAFT */ settingsAppearanceNav: '言語と表示',
  /* AI DRAFT */ settingsLanguageTitle: '表示言語',
  /* AI DRAFT */ settingsLanguageDescription: 'brSE.ai を表示する言語を選択します。',
  /* AI DRAFT */ settingsThemeTitle: '表示モード',
  /* AI DRAFT */ settingsThemeDescription: 'ライトまたはダークの表示を選択します。',
  settingsBacklogCardTitle: 'Backlog',
  /* AI DRAFT */ settingsBacklogPushDescription:
    '議事録のアクションアイテムは、以下のアカウントで Backlog に送信されます。',
  /* AI DRAFT */ settingsBacklogConnectedSince: '{date} から接続',
  /* AI DRAFT */ settingsBacklogDisconnect: '接続を解除',
  /* AI DRAFT */ settingsBacklogDisconnecting: '解除しています…',
  /* AI DRAFT */ settingsBacklogDisconnectConfirm:
    'この Backlog アカウントの接続を解除しますか？再度接続するまで、アクションアイテムの送信はできなくなります。',
  /* AI DRAFT */ settingsBacklogDisconnectNote:
    'brSE.ai 側のアクセスのみを解除します。Backlog 側の承認は Backlog で解除してください。',
  /* AI DRAFT */ settingsBacklogStatusConnected: '接続済み',
  /* AI DRAFT */ settingsBacklogStatusNotConnected: '未接続',
  /* AI DRAFT */ settingsBacklogCapabilities: 'brSE.ai が行うこと',
  /* AI DRAFT */ settingsBacklogCanProjects: '参加しているプロジェクトの一覧を参照します',
  /* AI DRAFT */ settingsBacklogCanTickets:
    'Push to Backlog を押したときにチケットを作成・更新します',
  /* AI DRAFT */ settingsBacklogCannot:
    'パスワードを参照することはなく、確認なしに送信することもありません',
  trash: 'ゴミ箱',
  startMeeting: '会議を開始',
  createMeeting: '会議を作成',

  // Glossary page
  glossarySystemDictionaries: 'システム辞書',
  glossaryYourDictionaries: '自分の辞書',
  glossaryTermsLabel: '用語',
  glossarySystemLabel: 'システム',
  glossaryUploadedLabel: 'アップロード日',
  glossaryDownload: 'ダウンロード',
  glossaryDownloadTemplate: 'テンプレートをダウンロード',
  glossaryUploadDictionary: '辞書をアップロード',
  glossaryUpload: 'アップロード',
  glossaryUploading: 'アップロード中...',
  glossaryUploadConstraints: '.csv のみ · 最大 5MB · 最大 2,000 行 · 2 列必須',
  glossaryUploadCsvOnly: '.csv ファイルのみアップロードできます。',
  glossaryUploadTooLarge: 'ファイルは 5MB 以下にしてください。',
  glossaryUploadFailed: '辞書のアップロードに失敗しました。もう一度お試しください。',
  glossaryUploadDragDrop: 'ドラッグ＆ドロップ、またはクリックしてファイルを選択',
  glossaryUploadProgress: '{total} 件中 {current} 件をアップロード中...',
  glossaryUploadFilesSkipped:
    '赤色のファイルはスキップされます。削除または差し替えるか、有効なファイルのみ続行してください。',
  glossaryUploadFile: '{count} 件をアップロード',
  glossaryUploadFiles: '{count} 件をアップロード',
  glossaryUploadNotCsv: '.csv ファイルではありません',
  glossaryUploadFileTooLarge: 'ファイルサイズが大きすぎます',
  glossaryUploadSummary: '{total} 件中 {uploaded} 件をアップロードしました',
  glossaryUploadSummaryHint: '下記のファイルを修正して、もう一度アップロードしてください。',
  glossaryUploadTermsAdded: '{count} 件の用語を追加',
  glossaryUploadRowsSkipped: '{count} 行をスキップ（文字数超過）',
  glossaryUploadRowSkipped: '1 行をスキップ（文字数超過）',
  glossaryUploadTermsConflict: '{count} 件の用語が競合',
  glossaryUploadTermConflict: '1 件の用語が競合',
  glossaryUploadTooManyRows: '行数が多すぎます — 最大 2,000 行',
  glossaryUploadWrongFormat: '形式が正しくありません',
  glossaryUploadEmptyCells: '空のセルがあります',
  glossaryUploadEmptyFile: '有効な用語がありません',
  glossaryUploadViewDetails: '詳細を表示',
  glossaryUploadHideDetails: '詳細を隠す',
  glossaryUploadChooseAnother: '別のファイルを選択',
  glossaryUploadDone: '完了',
  glossaryUploadRowDetail: '{row} 行目 · {field}: {reason}',
  glossaryUploadConflictDetail: '{term} · 行 {rows} · 訳語: {meanings}',
  glossaryUploadColumnExpected:
    '{column} 列目は「{expected}」ではなく「{actual}」になっています。',
  glossaryUploadColumnCount:
    '{expected} 列が必要ですが、{actual} 列見つかりました。',
  glossaryUploadReasonTooLong: '100 文字を超えています',
  glossaryUploadFieldTerm: '言語 1',
  glossaryUploadFieldMeaning: '言語 2',
  glossaryRenameFailed: '辞書名を変更できませんでした。名前を確認してもう一度お試しください。',
  glossaryReorderHint:
    '鉛筆アイコンで名前を変更できます。∷ をドラッグするか矢印で優先順位を変更できます。Default をオンにすると新しい会議で自動選択されます — 会議ごとに変更もできます。',
  glossaryDefaultLabel: 'Default',
  glossaryEmptyUser: '辞書がありません。CSV をアップロードして開始してください。',
  glossaryRename: '辞書名を変更',
  glossaryMoveUp: '上へ移動',
  glossaryMoveDown: '下へ移動',
  glossaryDragHandle: 'ドラッグして並び替え',
  glossaryDeleteConfirm: 'この辞書を削除しますか？',
  glossaryActionFailed: '問題が発生しました。もう一度お試しください。',

  myMeetings: '会議一覧',
  hello: 'こんにちは、{name}さん！',
  /* AI DRAFT — needs a native-speaker review before release. */
  ongoingMeetings: '進行中の会議',
  /* AI DRAFT — needs a native-speaker review before release. */
  meetingLiveBadge: '進行中',
  /* AI DRAFT — needs a native-speaker review before release. */
  meetingInviteFrom: '{name} さんからの招待',
  recentMeetings: '最近の会議',
  viewAll: 'すべて表示 →',
  upcoming: '予定',
  upcomingSectionTitle: '予定されている会議',
  upcomingBadge: '予定',
  noMeetingsYet: '会議がありません',
  noMeetingsHint: '会議を作成して、翻訳と議事録の生成を始めましょう。',
  noUpcomingMeetings: '予定されている会議はありません',
  noUpcomingMeetingsHint: '開始準備が整った会議がここに表示されます。',
  hasMinutes: '議事録あり',
  noMinutes: '議事録なし',
  failedLoadMeetings: '会議の読み込みに失敗しました',
  tabAudioCaptureFailed:
    'タブの音声共有が有効になっていません。録音プロンプトでタブを選択し、音声共有を許可してください。',

  createNewMeeting: '新しい会議を作成',
  createMeetingSubtitle: 'リアルタイム翻訳環境を設定します。',
  closeCreateMeetingModal: '会議作成モーダルを閉じる',
  meetingName: '会議名',
  meetingNameRequired: '会議名は必須です。',
  meetingNameTooLong: '会議名は255文字以内で入力してください。',
  meetingJoinUrlRequired: 'ネイティブ文字起こしには会議の参加URLが必要です。',
  meetingJoinUrlLabel: '会議の参加URL',
  meetingJoinUrlHint:
    'ネイティブモードでは会議の参加者リストから話者を識別します。ボットを会議に参加させる必要があります。',
  transcriptionSourceLabel: '文字起こしのソース',
  transcriptionSourceBrowser: 'ブラウザ音声',
  transcriptionSourceTeams: 'Microsoft Teams',
  transcriptionSourceGoogleMeet: 'Google Meet',
  /* AI DRAFT — unreviewed Japanese */
  createMeetingStepDetails: '会議の詳細',
  /* AI DRAFT — unreviewed Japanese */
  createMeetingStepType: '会議の種類',
  /* AI DRAFT — unreviewed Japanese */
  createMeetingStepLanguage: '言語',
  /* AI DRAFT — unreviewed Japanese */
  meetingTypeOffline: 'オフライン会議',
  /* AI DRAFT — unreviewed Japanese */
  meetingTypeOfflineDesc: 'AI がマイクから聞き取ります',
  /* AI DRAFT — unreviewed Japanese */
  meetingTypeOnline: 'オンライン会議',
  /* AI DRAFT — unreviewed Japanese */
  meetingTypeOnlineDesc: '音声付きでタブ・ウィンドウ・画面を共有します',
  /* AI DRAFT — unreviewed Japanese */
  meetingTypeTeamsDesc: 'AI ボットが会議リンクから参加します',
  /* AI DRAFT — unreviewed Japanese */
  meetingTypeGoogleMeetDesc: 'AI ボットが会議リンクから参加します',
  /* AI DRAFT — unreviewed Japanese */
  shareTabAudioCallout:
    '開始を押したあと、共有するタブ・ウィンドウ・画面を選び、「タブの音声を共有」（または「システム音声を共有」）をオンにしてください。AI が会議を聞き取れるようになります。',
  /* AI DRAFT — unreviewed Japanese */
  createMeetingHelpTitle: 'AI の理解を助ける',
  createMeetingHelpOptional: '(任意)',
  /* AI DRAFT — unreviewed Japanese */
  createMeetingHelpSubtitle:
    '名前・役割・用語を追加すると、翻訳と議事録の精度が上がります。',
  transcriptionSourceGoogleMeetPreviewRequired:
    'Google Meet Media API の開発者プレビューが必要です',
  connectGoogleMeetMedia: 'Google Meet Media を接続',
  meetingNameTaken: '同じ名前の会議がすでに存在します。別の名前を入力してください。',
  meetingNameTakenInTrash:
    'ゴミ箱にある会議がこの名前を使用しています。復元するか、完全に削除するか、別の名前を入力してください。',
  meetingNamePlaceholder: '例: Meeting#3',
  meetingNameResolving: '名前を生成しています…',
  defaultMeetingName: 'Meeting#{n}',
  sessionKindLabel: 'モード',
  /* AI DRAFT — needs a native-speaker review before release. */
  sessionKindSelectionHint:
    '対面でのインタビューにはインタビューモードを、会議の録音のみが必要な場合は社内会議モードを選択してください。',
  /* AI DRAFT — needs a native-speaker review before release. */
  sessionKindMeeting: '通常会議',
  sessionKindInterview: 'インタビュー',
  /* AI DRAFT — unreviewed Japanese */
  sessionKindPresentation: 'プレゼンテーション',
  /* AI DRAFT — unreviewed Japanese */
  sessionKindInternalMeeting: '社内会議',
  /* AI DRAFT — unreviewed Japanese */
  meetingModeMismatch:
    'サーバーが選択したモードを確認できませんでした。別の種類として記録しないよう、会議を停止しました。',
  /* AI DRAFT — unreviewed Japanese */
  minutesJobPollFailed: '議事録の状態を確認できませんでした。再試行します…',
  /* AI DRAFT — unreviewed Japanese */
  internalMeetingNeedsTargetLang: '社内会議を開始する前に会議の言語を選択してください。',
  /* AI DRAFT — unreviewed Japanese */
  internalMeetingNoNativeMode: '社内会議に会議ボットは参加できません。',
  /* AI DRAFT — unreviewed Japanese */
  internalMeetingNoSttProvider:
    '社内会議は文字起こしエンジンを自動で選択します。選択を解除してください。',
  /* AI DRAFT — unreviewed Japanese */
  internalMeetingRecording: '録音中です。会議が終了するまでここには何も表示されません。',
  /* AI DRAFT — unreviewed Japanese */
  internalMeetingPaused: '一時停止中です。録音は行われていません。',
  /* AI DRAFT — unreviewed Japanese */
  internalMeetingQueuePending: '会議が終了しました。議事録を順番待ちしています…',
  /* AI DRAFT — unreviewed Japanese */
  internalMeetingQueueRunning: '議事録を作成しています…',
  /* AI DRAFT — unreviewed Japanese */
  internalMeetingTruncated:
    '録音が上限に達したため、この議事録は会議の前半のみを対象としています。',
  /* AI DRAFT — unreviewed Japanese */
  internalMeetingFailed: '議事録を作成できませんでした。',
  /* AI DRAFT — unreviewed Japanese */
  internalMeetingExpired:
    'この議事録はすでに利用できません。保持期間が短く、再作成もできません。',
  recordMeeting: '録音を開始',
  meetingLanguage: '会議の言語',
  translationLabel: '翻訳',
  translateOneWay: '一方向翻訳',
  translateOneWayDesc: '他の参加者の発言をあなたの言語に翻訳します',
  translateTwoWay: '双方向翻訳',
  translateTwoWayDesc: '会話の両側をリアルタイムで翻訳します',
  microphone: 'マイク',
  defaultMicrophone: 'デフォルトマイク',
  microphoneN: 'マイク {n}',
  microphoneLoadFailed: 'マイクデバイスを読み込めませんでした。',
  noMicrophoneDetected: 'マイクが検出されません。マイクなしで会議を開始します。',
  shareTabAudioHint:
    '録音プロンプトが表示されたら、ブラウザタブを選択し、タブの音声共有を有効にしてください',
  /* AI DRAFT — needs a native-speaker review before release. */
  sendScreenFramesHint:
    '共有中のタブの画像を数秒ごとにAIへ送信し、画面の内容も読み取れるようにします',
  transcriptionLanguage: '文字起こし言語（入力）',
  translationLanguage: '翻訳言語（出力）',
  languageEnglish: '英語',
  languageJapanese: '日本語',
  languageVietnamese: 'ベトナム語',
  sttProviderSonioxLabel: 'Soniox',
  sttProviderSonioxDesc: 'デフォルト',
  sttProviderAzureLabel: 'Azure',
  sttProviderAzureDesc: 'Microsoft Azure AI',
  sttProviderGoogleLabel: 'Google',
  sttProviderGoogleDesc: 'Gemini Live STT + LLM',
  sttProviderChatgptLabel: 'Azure OpenAI',
  sttProviderChatgptDesc: 'GPT Realtime Transcribe',
  autoDetect: '自動検出',
  autoDetectBadge: '自動検出',
  contextOptional: '補足情報（任意）',
  contextMeetingDescription: '会議内容の説明',
  contextFileDragDrop: 'ドラッグ＆ドロップ、またはクリックしてファイルを選択',
  contextFileSelect: 'ファイルを選択',
  contextFileFormats: 'PDF · DOCX · PPTX · XLSX · HTML · 最大 20MB',
  contextFileAndOr: 'または',
  contextFileInvalidType: 'PDF、DOCX、PPTX、XLSX、HTML のみアップロードできます。',
  contextFileTooLarge: '各ファイルは 20MB 以下にしてください。',
  contextFileInvalidTypeAndSize:
    'PDF、DOCX、PPTX、XLSX、HTML のみ、各 20MB 以下でアップロードできます。',
  contextAnalyzing: 'コンテキストを解析中...',
  contextAnalyzeFailed: '会議ファイルを解析できませんでした。もう一度お試しください。',
  contextFileStatusDone: '完了',
  removeContextFile: 'ファイルを削除',
  enterText: 'テキストを入力',
  glossary: '用語集',
  upgradeNow: '今すぐアップグレード',
  glossaryAiIt: 'AI・IT（適用済み）',
  glossarySelectPlaceholder: '用語集を選択',
  glossarySystemAppliedAutomatically: 'IT 用語集は自動的に適用されます。',
  // Backlog project picker (BRAI-506)
  /* AI DRAFT — needs a native-speaker review before release. */
  // ── Weekly report (BRAI-772) ──
  weeklyReportTab: '週次レポート',
  weeklyReportWeekLabel: 'レポート週（金〜木）',
  weeklyKpiWeekLabel: 'KPI週（月〜日）',
  weeklyReportPreviousWeek: '前の週',
  weeklyReportNextWeek: '次の週',
  weeklyReportGenerate: 'レポートを作成',
  weeklyReportRegenerate: '作り直す',
  weeklyReportGenerating: '週のデータを読み、要約を書いています。最大1分ほどかかります。',
  weeklyReportEmpty: 'この週のレポートはまだ作成されていません。',
  weeklyReportSavedAt: '作成日時 {at}',
  weeklyReportSummaryHeading: '要約',
  weeklyReportWorkHeading: '今週の作業',
  weeklyReportBugsHeading: '追跡中のバグ',
  weeklyReportPlanHeading: '来週の予定',
  weeklyReportMeetingsHeading: '会議',
  weeklyReportWorkCounts: '{total} 件中 {done} 件完了 ・ 進行中 {inProgress} 件',
  weeklyReportBugCounts: '追跡 {tracked} ・ クローズ {closed} ・ 進行中 {inProgress} ・ High {high}',
  weeklyReportListTruncated: '{total} 件中 {shown} 件を表示しています。上の件数は全件が対象です。',
  weeklyReportNoWork: 'この週にチケットの動きはありませんでした。',
  weeklyReportNoBugs: 'この週に作成・クローズされたバグはありません。',
  weeklyReportNoPlan: 'チケットにも議事録にも、来週の予定はまだありません。',
  weeklyReportNoMeetings: 'この週にご自身が作成した議事録付きの会議はありません。',
  weeklyReportTicketsUnavailable:
    'Backlog のチケットを読み取れなかったため、作業・バグ・チケット由来の予定が欠けています。作業がなかったわけではありません。',
  weeklyReportDegraded:
    '要約の生成に失敗しました。以下の件数はすべて正しい値です。要約が必要な場合は作り直してください。',
  weeklyReportExportSlides: 'スライドを書き出す',
  weeklyReportExporting: 'スライドを作成中…',
  weeklyPlanSourceDueDate: '期限',
  weeklyPlanSourceMilestone: 'マイルストーン',
  weeklyPlanSourceMeeting: '会議から',
  weeklyKpiHeading: 'KPIフォーム',
  weeklyKpiDraft: 'AIで下書き',
  weeklyKpiDrafting: '下書き中…',
  weeklyKpiSave: 'KPIを保存',
  weeklyKpiSaving: '保存中…',
  weeklyKpiEmpty: 'この週のKPIフォームはまだ保存されていません。',
  weeklyKpiEdited: '編集内容を保存済み',
  weeklyKpiOverallStatus: '全体ステータス',
  weeklyKpiAchievement: '今週の成果',
  weeklyKpiIssues: '課題',
  weeklyKpiRisk: 'リスク',
  weeklyKpiSupport: '必要な支援・判断',
  weeklyKpiHealth: '健全性',
  weeklyKpiFollowUp: 'フォローアップ状況',
  weeklyKpiNone: 'なし',
  weeklyKpiNotAssessed: '未評価 — チケットを読み取れませんでした',
  failedGenerateWeeklyReport: '週次レポートを作成できませんでした。',
  failedLoadWeeklyReport: '週次レポートを読み込めませんでした。',
  failedGenerateWeeklyKpi: 'KPIの下書きを作成できませんでした。',
  failedSaveWeeklyKpi: 'KPIフォームを保存できませんでした。',
  failedExportWeeklySlides: 'スライドを作成できませんでした。',
  projectLabel: 'プロジェクト',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectPlaceholder: 'プロジェクトなし',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectOther: 'その他',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectLoading: 'プロジェクトを読み込み中…',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectNone: '選択できるプロジェクトがありません',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectNoneHint: 'Backlog スペースに有効なプロジェクトがありません。',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectLoadFailed: 'プロジェクトを取得できません',
  /* AI DRAFT — needs a native-speaker review before release. */
  failedLoadProjects: 'プロジェクトを読み込めませんでした。',
  /* AI DRAFT — needs a native-speaker review before release. */
  failedLoadProjectContext: 'プロジェクトコンテキストを読み込めませんでした。',
  /* AI DRAFT — needs a native-speaker review before release. */
  failedSaveProjectContext: 'プロジェクトコンテキストを保存できませんでした。',
  /* AI DRAFT — needs a native-speaker review before release. */
  failedLoadProjectContextVersions: 'コンテキストのバージョンを読み込めませんでした。',
  /* AI DRAFT — needs a native-speaker review before release. */
  failedRestoreProjectContextVersion: 'このコンテキストバージョンを復元できませんでした。',
  /* AI DRAFT — needs a native-speaker review before release. */
  failedLoadRoomHistory: '参加前の内容を読み込めませんでした。',
  /* AI DRAFT — needs a native-speaker review before release. */
  failedSaveRoomHistory: 'このルームの最新の内容を保存できませんでした。',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectSearch: 'プロジェクトを検索',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectSearchEmpty: '該当するプロジェクトがありません',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectConnectRequired: 'Backlog に接続してプロジェクトを選択',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectReauthRequired: 'Backlog が API キーを受け付けません',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectConnectHint: '表示されるのは、ご自身の Backlog アカウントで参照できるプロジェクトです。',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectOpenSettings: '設定を開く',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectManagement: 'プロジェクト管理',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectsHeading: 'マイプロジェクト',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectsAccessSummary: 'アクセスできるプロジェクト {count} 件',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectsAccessSummaryOne: 'アクセスできるプロジェクト 1 件',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectsDirectorySearch: 'プロジェクト名またはキーで検索',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectsCreate: 'プロジェクトを作成',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectsMeetingCount: '会議 {count} 件',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectsMeetingCountOne: '会議 1 件',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectsDetails: '詳細',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectsOpenDetail: '{name} を開く',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectsRange: '全{total}件中 {start}-{end}件を表示',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailBack: 'プロジェクト一覧へ戻る',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailOverview: '概要',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailMembers: 'メンバー',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailMeetings: '会議',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailName: 'プロジェクト名',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailKey: 'プロジェクトキー',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailMetrics: 'プロジェクト指標',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailTotalMeetings: '合計: 記録済み会議 {count} 件',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailTotalMeetingsOne: '合計: 記録済み会議 1 件',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailLastMeeting: '最終会議: {when}',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailNoMeetings: 'まだ記録された会議はありません',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailOpenInBacklog: 'Backlog で開く',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailNotFound: 'そのプロジェクトは存在しないか、ご自身の Backlog アカウントでは参照できません。',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailMembersFailed: 'メンバー一覧を取得できませんでした。',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailNoMembers: 'このプロジェクトにメンバーはいません。',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailMemberYou: '自分',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailMemberRoleAdmin: '管理者',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailMemberRoleMember: 'メンバー',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailMembersRange: '全{total}件中 {start}–{end}件のメンバーを表示',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailMemberEmailMissing: '—',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailMeetingsEmpty: 'このプロジェクトの会議はまだありません。',
  projectDetailMeetingsHeading: 'プロジェクトの会議',
  projectDetailMeetingsSearchPlaceholder: '会議名・トピックで検索',
  projectDetailMeetingsRange: '全{total}件中 {start}–{end}件の会議を表示',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailContextTab: 'コンテキスト＆辞書',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailStatusRunning: '進行中',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailContextSubtab: 'プロジェクトコンテキスト',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictionarySubtab: 'プロジェクト辞書',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailContextTitle: 'プロジェクトコンテキスト',
  /* AI DRAFT — needs a native-speaker review before release. */
  failedSaveProjectAiVersion: 'AI バージョンを保存できませんでした。',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailContextHint:
    'このプロジェクトのすべての会議で、翻訳アシストと用語説明に自動で使われます。',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailContextAiVersionLabel: 'AI バージョン',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailContextEditAiVersion: 'AI バージョンを編集',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailContextSave: '保存',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailContextCancel: 'キャンセル',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailContextEmpty:
    'AI バージョンはまだありません。編集して会議の背景を追加してください。',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailContextInUse: '現在のバージョンを使用中',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailContextUpdatedAt: '更新',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailContextLatestHint: '最新バージョンを自動使用',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailContextLatestFrom: '作成元',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailContextLatestBadge: '最新',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailVersionsTitle: 'コンテキストのバージョン',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailVersionsHint: 'AI バージョンが変わるたびに、会議または編集から新しいバージョンが作成されます。変更内容を確認したり、以前のバージョンを復元できます。',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailVersionsEmpty: 'バージョンはまだありません。AI バージョンが変わるたびに作成されます。',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailVersionsColVersion: '版',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailVersionsColSource: 'ソース',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailVersionsColDate: '日付',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailVersionsColChanges: '変更内容',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailVersionsColStatus: 'ステータス',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailVersionsColActions: '操作',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailVersionsActive: '使用中',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailVersionsReplaced: '置き換え済み',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailVersionsView: '表示',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailVersionsRestore: '復元',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailVersionModalTitle: 'バージョン詳細',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailVersionCloseDialog: 'バージョン詳細を閉じる',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailVersionCreatedFrom: '作成元',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailVersionVs: '比較',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailVersionChanges: '件の変更',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailVersionAddedLegend: '追加・変更された情報',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailVersionRemovedLegend: '削除された情報',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailVersionRestore: 'このバージョンを復元',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailVersionAiProfileLabel: 'AI バージョン',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailVersionSourceHuman: '人が編集',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailVersionSourceManual: '手動編集',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailVersionSourceRestore: '復元されたバージョン',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailVersionUnknownMeeting: '会議',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictionaryEmpty: '辞書用語はまだありません。',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictSearchPlaceholder: '用語を検索...',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictSourceAll: 'すべての出典',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictSourceManual: '手動',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictSourceAi: 'AI 提案',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictImportCsv: 'CSV を取り込む',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictImportEmpty: 'そのファイルに用語の組が見つかりませんでした。',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictReviewTitle: 'AI 提案用語のレビュー',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictReviewEmpty: 'レビューする項目はまだありません。会議の後に AI の提案がここに表示されます。',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictReviewAdd: '辞書に追加',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictReviewSkip: 'スキップ',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictReviewUnblock: 'ブロック解除',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictReviewMeetings: '件の会議',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictReviewLastSeen: '最終',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictReviewAppearedSummary: '{count} 件の会議で出現 · 最終 {lastSeen}',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictReviewAppearedCountOnly: '{count} 件の会議で出現',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictReviewConfidenceHigh: '信頼度 高',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictReviewConfidenceMedium: '信頼度 中',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictReviewConfidenceLow: '信頼度 低',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictAddTerm: '用語を追加',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictAddTermsTitle: '用語を追加',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictAddRow: '行を追加',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictRemoveRow: '行を削除',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictCloseModal: '閉じる',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictEditTermTitle: '用語を編集',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictEditTermHint: '変更は以降のプロジェクト会議に適用されます。',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictDeleteTitle: 'この用語を削除しますか？',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictDeleteTermHeading: '用語',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictDeleteBody:
    '用語はプロジェクト辞書から削除されます。以降の会議の翻訳では AI はこの用語を使いません。この操作は取り消せません。',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictColTerm: '用語 (JA)',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictColTarget: '用語 (VI)',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictColTargetLang: '言語',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictColSource: '出典',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictColStatus: '状態',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictColActions: '操作',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictEditTerm: '用語を編集',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictDeleteTerm: '用語を削除',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictNoMatches: 'この条件に一致する用語はありません。',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictLearnedTitle: 'AI 学習用語',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictBlock: 'ブロック',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictUnblock: 'ブロック解除',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictStatusUsed: '使用中',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictStatusPending: '追加の会議が必要',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictStatusExpired: '期限切れ',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictStatusBlocked: 'ブロック済み',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictStatusDeclared: '手動登録済み',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictStatusOverCap: '上限超過',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictStatusInvalid: '無効',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictStatusSuggested: 'AIの提案',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictTermSource: '用語（原文）',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictTermTarget: '用語（訳語）',
  /* AI DRAFT — needs a native-speaker review before release. */
  projectDetailDictTermLang: '対象言語',
  /* AI DRAFT — needs a native-speaker review before release. */
  settingsBacklogDescription: 'プロジェクトの選択と Backlog への同期は、このキーで実行されます。',
  glossaryLoadingDictionaries: '用語集を読み込み中...',
  glossaryLoadDictionariesFailed: '用語集を読み込めませんでした。',
  glossaryNoDictionaries: '利用可能なユーザー用語集はありません。',
  glossaryRetry: '再試行',
  glossaryConflictWarning:
    '選択した用語集で {count} 件の用語競合が見つかりました。優先順位の高い用語集を適用します。',
  glossaryApplyFailed:
    '選択した用語集を適用できませんでした。選択内容を確認して、もう一度お試しください。',
  translationHard: '今すぐ翻訳',
  translationHardHint: 'Space',
  translationHardSent: 'AIに送信しました。',
  translationHardFailed: 'セッションが開いていないため送信されませんでした。',

  contextPlaceholder: '例: 製品名、参加者の役割、専門用語（日本語 ↔ ベトナム語）…',

  meetingHistory: '会議履歴',
  meetingTrash: '削除済みの会議',
  historyBreadcrumb: 'ホーム',
  historySubtitle: '過去の会議の翻訳内容とメモを管理・確認できます。',
  trashSubtitle: '削除された会議を確認できます。復元または完全削除が可能です。',
  activeTab: '有効',
  deletedTab: '削除済み',
  searchPlaceholder: 'キーワードで検索...',
  filterByDate: '日付で絞り込み',
  datePlaceholder: 'yyyy/mm/dd',
  /* AI DRAFT — needs a native-speaker review before release. */
  filterByDirection: '出力言語で絞り込み',
  allDirections: 'すべての出力言語',
  directionToJapanese: '日本語へ',
  directionToEnglish: '英語へ',
  directionToVietnamese: 'ベトナム語へ',
  advancedFilters: '詳細フィルター',
  meetingNameCol: '会議名',
  /* AI DRAFT */ dateTimeCol: '会議日時',
  deletedDateCol: '削除日時',
  durationCol: '所要時間',
  directionCol: '翻訳方向',
  remainingCol: '残り日数',
  statusCol: 'ステータス',
  actionCol: '操作',
  deletedStatus: '削除済み',
  notAvailable: '利用不可',
  noDeletedMeetings: '削除された会議はありません。',
  noMeetingsFound: '会議が見つかりません。',
  emptyTrash: 'ゴミ箱は空です',
  select: '選択',
  selectAll: 'すべて選択',
  selectMeetingAria: '「{title}」を選択',
  deleteSelected: '選択した会議を削除',
  deleteAllPermanently: 'すべて完全に削除',
  remainingDays: '残り{count}日',
  restore: '復元',
  permanentDelete: '完全削除',
  meetingUnfinishedBadge: '未完了',
  meetingUnfinishedTitle: 'この会議は正常に終了されていません',
  meetingUnfinishedBody:
    'この会議は正常に終了されなかったため、文字起こしと議事録は保存されていません。',
  showingRangeEmpty: '0件',
  showingRange: '全{total}件中 {start}-{end}件を表示',
  previousPage: '前のページ',
  nextPage: '次のページ',
  pageN: '{n}ページ',
  failedDeleteMeeting: '会議の削除に失敗しました',
  failedRestoreMeeting: '会議の復元に失敗しました',
  failedPermanentDeleteMeeting: '会議の完全削除に失敗しました',
  failedPermanentDeleteMeetings: '会議の一括完全削除に失敗しました',
  deleteMeetingTitle: '会議を削除しますか？',
  deleteMeetingBody:
    '「{title}」は削除済み会議に移動されます。後で削除済みリストから確認できます。',
  closeDeleteMeetingModal: '削除確認モーダルを閉じる',
  deleteSelectedMeetingsTitle: '{count}件の会議を削除しますか？',
  deleteSelectedMeetingsBody:
    '選択した{count}件の会議は削除済み会議に移動されます。後で削除済みリストから確認できます。',
  permanentDeleteMeetingTitle: '会議を完全に削除しますか？',
  permanentDeleteMeetingBody: '「{title}」は完全に削除されます。この操作は取り消せません。',
  permanentDeleteSelectedMeetingsTitle: '{count}件の会議を完全に削除しますか？',
  permanentDeleteSelectedMeetingsBody:
    '選択した{count}件の会議は完全に削除されます。この操作は取り消せません。',

  selectMeeting: '会議を選択して開始',
  aiTranslating: 'BrSE AIが翻訳中',
  readyToTranslate: '翻訳の準備完了',
  finishingTranslations: '共有が終了しました。最後の翻訳を受信しています…',
  statusConnecting: '接続中...',
  statusWaitingConsent: '参加の承認待ち',
  statusStopping: '停止中...',
  statusInterrupted: '接続が切断されました。録音を一時停止中',
  statusEnded: '会議終了',
  statusFailed: 'セッションに失敗しました',
  translationLanguageAria: '翻訳言語',
  failedStartMeeting: '会議の開始に失敗しました',
  mute: 'ミュート',
  unmute: 'ミュート解除',
  pause: '一時停止',
  resume: '再開',
  starting: '開始中...',
  startMeetingTitle: '会議を開始',
  /* AI DRAFT */ startMeetingHint: '「開始」を押すと録音と文字起こしが始まります',
  /* AI DRAFT */ copilotActionHint: 'ここまでの会話について Copilot に質問できます',
  /* AI DRAFT */ copilotNewItemsHint: 'Copilot に新しい提案があります',
  aiLivePaused: 'AI一時停止中',
  aiLiveTranslating: 'AI翻訳中',
  aiLiveReady: 'AI準備完了',
  recordingStatus: '録音中',
  keyPoints: '要点',
  hideKeyPoints: '要点を非表示',
  showKeyPoints: '要点を表示',
  endMeeting: '会議を終了',
  languageSwitchFailed: '翻訳言語を切り替えられませんでした。{language}に戻しました。',
  languageSwitchTimeout: 'サーバーが言語の切り替えを確認しませんでした。もう一度お試しください。',
  finalizingMeeting: '議事録を生成しています。しばらくお待ちください...',
  leaveWhileRecordingPrompt:
    '録音中です。このページを離れると会議が保存されない可能性があります。続行しますか？',
  unsavedMinutesChangesPrompt:
    '議事録に未保存の変更があります。このページを離れると変更は失われます。続行しますか？',
  failedSaveMeeting: '会議の保存に失敗しました',
  meetingSavedToast: '会議を履歴に保存しました。',
  meetingSaveFailedToast: '会議の保存に失敗しました。もう一度お試しください。',
  meetingMinutesUnavailable:
    '議事録はまだ利用できません。再試行して生成してください。',
  meetingMinutesNoSpeech:
    '録音から音声が検出されなかったため、議事録を作成できませんでした。',
  meetingProjectMoveOpen: 'プロジェクトを変更',
  meetingProjectMoveBusyTooltip:
    '議事録を生成中です。完了後にプロジェクトを変更できます。',
  meetingProjectMoveConfirm:
    'この会議を {from} から {to} へ移動しますか？{from} はこの会議から学習した内容を忘れ、代わりに {to} が学習します。',
  meetingProjectMoveTicketsNote:
    '{from} で作成済みの Backlog 課題は {from} に残ります。移動も再作成もされません。',
  meetingProjectMoveRegenerateNote:
    '再生成すると、手作業で編集した内容も含めて現在の議事録が置き換わります。',
  meetingProjectMoveAndRegenerate: '移動して議事録を再生成',
  meetingProjectMoveOnly: '移動のみ',
  meetingProjectMove: '移動',
  meetingProjectMoveFailed: 'この会議を指定のプロジェクトへ移動できませんでした。',
  offlineWarningTitle: 'インターネット接続がありません',
  offlineWarningBody:
    '録音はローカルで継続しています。会議を終了する前に、接続が回復するまでお待ちください。',
  offlineEndDisabledTooltip: 'オフライン中は会議を終了できません。接続を回復してください。',
  offlineRestoredToast: 'オンラインに戻りました。会議を終了して保存できます。',
  offlineCannotEndTitle: 'オフライン中は保存できません',
  offlineCannotEndBody:
    '保存にはインターネット接続が必要です。文字起こしと翻訳は再接続までローカルに保持されます。',

  meetingNotFound: '会議が見つかりません',
  loadingMeeting: '会議を読み込み中...',
  backToMeetings: '会議一覧へ戻る',
  contentSummary: '内容の要約',
  noSummaryAvailable: 'この会議の要約はまだありません。',
  noTranslationSummaryAvailable: '翻訳要約はまだありません。',
  clickToEdit: 'クリックして編集',
  actionItems: 'アクション項目',
  confirmAction: '要確認事項',
  bilingualTranscript: 'バイリンガル文字起こし',
  /* AI DRAFT */ noTranslationYet: 'まだ翻訳されていません',
  noTranscriptCaptured: 'この会議では文字起こしが記録されていません。',
  time: '時間',
  speaker: '話者',
  showLessTranscript: '文字起こしを少なく表示 ↑',
  viewFullTranscript: '文字起こしをすべて表示 →',
  duration: '時間',
  translated: '翻訳語数',
  exportDocx: 'DOCXをエクスポート',
  sendEmail: '議事録をメールで共有',
  shareMinutesEmailDescription:
    '「{meetingName}」の議事録をメールの添付ファイルとして送信します。',
  recipientEmail: '宛先メールアドレス',
  recipientEmails: '宛先メールアドレス',
  recipientEmailPlaceholder: 'name@company.com',
  send: '送信',
  sendAnyway: 'それでも送信',
  shareMinutesExternalDomainWarning:
    'このメールアドレスは社内ドメイン外です。本当にこの会議の議事録を送信しますか？',
  shareMinutesExternalDomainWarningOne:
    '1件のメールアドレスが社内ドメイン外です。本当にこの会議の議事録を送信しますか？',
  shareMinutesExternalDomainWarningMany:
    '{count}件のメールアドレスが社内ドメイン外です。本当にこの会議の議事録を送信しますか？',
  failedShareMinutesEmail: '議事録メールの送信に失敗しました。',
  sendingEmail: '送信中...',
  emailSentTitle: 'メールを送信しました',
  emailSentBody: '「{meetingName}」の議事録を {email} に送信しました。',
  emailSentBodyMulti: '「{meetingName}」の議事録を {emails} に送信しました。',
  done: '完了',
  automaticallySavedToHistory: '履歴に自動保存されました。',
  meetingRecording: '会議録音',
  audioPlayback: '音声再生',
  audioSeekAria: '再生位置を移動',
  skipBackAria: '10秒戻る',
  skipForwardAria: '10秒進む',
  playbackSpeedAria: '再生速度を変更',
  audioUnsupported: 'お使いのブラウザは音声再生に対応していません。',
  audioUnavailableTitle: '音声を利用できません',
  audioUnavailableHint: 'この会議の録音音声を保存できませんでした。',
  audioPlaybackError: 'この音声を読み込めませんでした。',
  yourFeedback: 'あなたのフィードバック',
  translationQualityScore: '翻訳品質: {rating}/5',
  noFeedbackProvided: 'フィードバックはまだありません。',
  addFeedbackAria: 'フィードバックを追加',
  yourRealtimeContext: '会議のコンテキスト',

  meetingMinutes: '議事録',
  meetingInfoSection: '会議情報',
  purposeSection: '目的',
  discussionTopicsSection: '議論内容',
  actionItemsSection: 'アクションアイテム',
  meetingInfoName: '会議名',
  meetingInfoDateTime: '日時',
  meetingInfoParticipants: '参加者',
  meetingInfoParticipantsUnavailable: '—',
  meetingInfoTranslationDirection: '翻訳方向',
  meetingInfoSttProvider: 'STTプロバイダー',
  meetingInfoCreatedBy: '作成者',
  meetingInfoCreatedByValue: 'BrSE.ai（AI自動生成）',
  meetingInfoCreatedDate: '作成日',
  purposeUnavailable: 'この会議の目的は特定されませんでした。',
  removeTopicAria: '議題を削除',
  topicsUnavailable: 'この会議の議論内容は特定されませんでした。',
  topicOverviewLabel: '概要',
  topicDiscussionsLabel: '主な議論',
  topicConclusionsLabel: '結論',
  topicTitlePlaceholder: '議題タイトル',
  actionItemsTableNo: 'No',
  actionItemsTableContent: '対応内容',
  actionItemsTableAssignee: '担当',
  actionItemsTableDeadline: '期限',
  /* AI DRAFT */ actionItemsTableIssueType: '種別',
  /* AI DRAFT */ backlogIssueTypeDefault: 'プロジェクト既定',
  assigneePlaceholder: '担当者を選択',
  /* AI DRAFT — unreviewed Japanese */
  assigneeSearch: '名前またはアドレスで検索',
  /* AI DRAFT — unreviewed Japanese */
  assigneeTenantGroup: '社内メンバー',
  /* AI DRAFT — unreviewed Japanese */
  assigneeEnterManually: 'その他…',
  /* AI DRAFT — unreviewed Japanese */
  assigneeBackToList: '一覧に戻る',
  assigneeNoResults: '該当するメンバーが見つかりません',
  /* AI DRAFT — unreviewed Japanese */
  assigneeLoading: 'メンバーを読み込み中…',
  /* AI DRAFT — unreviewed Japanese */
  assigneeRetry: '再試行',
  membersLoadError: 'メンバー一覧を読み込めませんでした。',
  addActionItemAria: 'アクションアイテムを追加',
  removeActionItemAria: 'アクションアイテムを削除',
  showAllActionItems: 'アクションアイテムをすべて表示（{count}件）',

  // Meeting detail — one Edit for the whole page
  /* AI DRAFT */ editingMeetingTitle: '編集中 - {title}',
  /* AI DRAFT */ unsavedChangeOne: '未保存の変更が1件',
  /* AI DRAFT */ unsavedChangesMany: '未保存の変更が{count}件',
  /* AI DRAFT */ addPurpose: '目的を追加',
  /* AI DRAFT */ addDiscussion: '議論を追加',
  /* AI DRAFT */ addConclusion: '結論を追加',
  /* AI DRAFT */ addTopic: 'トピックを追加',
  /* AI DRAFT */ addAction: 'アクションを追加',
  /* AI DRAFT */ addShort: '追加',
  /* AI DRAFT */ discussionsUnavailable: '議論はまだありません。',
  /* AI DRAFT */ conclusionsUnavailable: '結論はまだありません。',
  /* AI DRAFT */ actionItemsEmptyHint: 'アクションはまだありません。「+ アクションを追加」から追加できます。',
  /* AI DRAFT */ actionItemsEmptyShort: 'アクションはまだありません。',
  /* AI DRAFT */ removePurposeAria: '目的を削除',
  /* AI DRAFT */ removeDiscussionAria: '議論を削除',
  /* AI DRAFT */ removeConclusionAria: '結論を削除',
  /* AI DRAFT */ collapseTopicAria: 'トピックを折りたたむ',
  /* AI DRAFT */ expandTopicAria: 'トピックを展開',
  /* AI DRAFT */ purposePlaceholder: '会議の目的を入力してください...',
  /* AI DRAFT */ topicOverviewPlaceholder: 'このトピックの概要を入力してください...',

  // Backlog sync (BRAI-464)
  /* AI DRAFT — needs a native-speaker review before release. */
  nextActionsSection: 'ネクストアクション',
  /* AI DRAFT */ nextActionsNeedsProject:
    'Backlog プロジェクトを選ぶと、アクションアイテムの一覧と課題登録ができます。',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogColType: '処理',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogTypeNew: '新規チケット',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogTypeUpdate: '{key} を更新',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogChecking: 'Backlog を照合中…',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogSync: 'Backlog に同期',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogSyncing: '同期中…',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogEditModeHint: '先に議事録を保存してください。同期は保存済みの内容を読み取ります。',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogDryRunNotice: 'サーバーはドライラン中です。Backlog には何も書き込まれません。',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogColAction: '処理',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogColTicket: 'チケット',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogColStatus: 'ステータス',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogMatchedHint:
    '既存のチケットと一致しました。上書きはまだ利用できません。当面は Backlog 側で更新してください。',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogSummary: '作成 {created} · 更新 {updated} · スキップ {skipped} · 失敗 {failed}',

  // The reviewed push (BRAI-575)
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogPush: 'Backlog に登録',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogColBacklog: 'Backlog',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogActionCreate: '新規作成',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogActionUpdate: '更新',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogRowExcluded: 'Backlog に登録しない',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogRowNotPushed: '未登録',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogRowSkipped: '対象外',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogRowDuplicate: '作成済み',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogRowFailed: '失敗',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogRowIncludeAria: 'アクションアイテム {number} を Backlog に登録する',
  /* AI DRAFT — needs a native-speaker review before release. */
  actionItemsNoDeadline: '未設定',

  // The confirmed overwrite (BRAI-575)
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogRowNeedsConfirm: '要確認',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogNothingToCreate: '新規作成する項目はありません。更新は各パネルから個別に実行します。',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogRowUpdated: '更新済み',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogActionCreated: '作成済み',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogConfirmTitle: '{key} を更新する前に確認してください',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogConfirmMatchHigh: '一致度が高い',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogConfirmMatchLow: '一致度が低い',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogConfirmMatchHelp:
    'このアクションアイテムが {key} と同じ作業である確度です。上書きに同意する前に下の項目をご確認ください。',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogConfirmColField: '項目',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogConfirmColCurrent: '現在',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogConfirmColNext: '更新後',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogFieldSummary: '件名',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogFieldPriority: '優先度',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogFieldAssignee: '担当者',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogFieldDueDate: '期限日',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogFieldDescription: '詳細',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogConfirmDescriptionEmpty: 'このチケットにはまだ詳細がありません。',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogConfirmShowAll: 'すべて表示',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogConfirmShowLess: '折りたたむ',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogConfirmDescriptionUnreadable: '現在の詳細を取得できませんでした。',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogConfirmStale: 'キャッシュから読み込んだ内容です。気になる場合はチケットをご確認ください。',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogConfirmBlocked: '何も書き込まれていません。下のチケット内容を確認して再度お試しください。',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogConfirmNoop: '変更点はありません。チケットはすでに一致しています。',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogConfirmMatchReason: 'このチケットを選んだ理由: {reason}',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogConfirmEditHint: '相違があれば「更新後」の列で直接編集してください。',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogConfirmReset: 'リセット',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogConfirmApply: '更新を確定',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogConfirmSkip: 'この件をスキップ',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogConfirmApplying: '更新中…',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogConfirmUnassigned: '担当者なし',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogConfirmKeepDueDate: '変更なし',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogSummaryBar:
    '作成 {created} · 更新 {updated} · 要確認 {confirm} · 作成済み {duplicate} · 対象外 {skipped} · 失敗 {failed}',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogSummaryRemaining: '#{number} を確認すると完了します',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogSummaryDone: '選択したものはすべて Backlog に書き込まれました。',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogViewIssue: 'チケットを開く',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogDismissResults: '閉じる',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogMembersUnavailable:
    'Backlog のメンバー一覧を取得できませんでした。議事録の名前を使って同期します。',
  /* AI DRAFT — needs a native-speaker review before release. */
  failedSyncBacklog: 'Backlog に同期できませんでした。',

  /* AI DRAFT — needs a native-speaker review before release. */
  backlogConnectionTitle: 'Backlog アカウント',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogConnectionConnected: '{name} として接続済み',
  /* AI DRAFT — needs a native-speaker review before release. */
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogConnectionNone: 'Backlog アカウントが接続されていません。',
  /* AI DRAFT — needs a native-speaker review before release. */
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogConnectionExpired: '{space} は現在の接続を受け付けなくなりました。',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogConnectionSpaceUnknown: 'Backlog スペースに現在アクセスできません。',
  /* AI DRAFT — needs a native-speaker review before release. */
  /* AI DRAFT — needs a native-speaker review before release. */
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogConnectWithBacklog: 'Backlog で接続',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogSsoHint: 'Backlog に移動してこのアプリケーションを承認すると、ここに戻ります。',
  /* AI DRAFT — needs a native-speaker review before release. */
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogConnecting: '確認中…',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogFinishingConnection: 'Backlog の接続を完了しています…',
  /* AI DRAFT — needs a native-speaker review before release. */
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogConnectionRequiredNotice:
    '接続すると、プロジェクト選択にご自身が参加しているプロジェクトが表示されます。',
  /* AI DRAFT — needs a native-speaker review before release. */
  backlogSyncNeedsConnection: 'Backlog アカウントを接続すると同期できます。',
  /* AI DRAFT */ backlogNudgeTitle: '新しい Backlog 機能',
  /* AI DRAFT */ backlogNudgeBody:
    'この機能はご自身の Backlog アカウントを使います。プロジェクトの選択やアクションアイテムの課題登録が必要なら接続してください。不要なら、このお知らせはスキップできます。',
  /* AI DRAFT */ backlogNudgeAction: '接続する',
  /* AI DRAFT */ backlogNudgeSkip: 'スキップ',
  /* AI DRAFT */ backlogNudgeDismiss: '閉じる',
  /* AI DRAFT */ backlogNudgeSidebar: 'Backlog アカウント未接続',
  /* AI DRAFT — needs a native-speaker review before release. */
  failedBacklogConnection: 'Backlog の接続状態を取得できませんでした。',
  /* AI DRAFT — needs a native-speaker review before release. */
  failedBacklogConnect: 'Backlog との接続を完了できませんでした。',
  showLessActionItems: '折りたたむ',

  input: '入力',
  output: '出力',
  panelTitleSeparator: '・',
  realTimeKeyPoints: 'リアルタイム要約',
  keyPointsPlaceholder: '会議中に要点が表示されます。',
  memoInsightsLoading: 'インサイトを生成中…',
  memoInsightsError: 'AIインサイトを読み込めませんでした。',
  actionItem: 'アクション項目',
  actionItemPlaceholder: '確認が必要なアクションは、文字起こしから自動で抽出されます。',
  memoActions: 'アクション',
  memoTasks: 'タスク',
  memoActionsUnavailable: 'この会議でアクションは特定されませんでした。',
  memoTasksUnavailable: 'この会議でタスクは特定されませんでした。',
  keyPointsUnavailable: 'この会議で要点は特定されませんでした。',
  liveMemo: 'ライブメモ',
  liveMainContent: '会議サマリー',
  editMainContent: '編集',
  saveMainContent: '保存',
  cancelMainContentEdit: 'キャンセル',
  mainContentSaveFailed: '編集内容を保存できませんでした。もう一度お試しください。',
  mainContentSaveTimeout: 'サーバーから編集内容の確認がありませんでした。もう一度お試しください。',
  liveMainContentPlaceholder: '会議が進むにつれて内容が表示されます。',
  liveOutputBatch: '出力（バッチ）',
  bilingualTimeline: '高精度モード',
  originalTranscript: '原文',
  realtimeTranslation: 'リアルタイム翻訳',
  batchTranslation: '高精度翻訳',
  listeningTranscript: '聞き取り中',
  unknownSpeaker: '不明な話者',
  // Shared room the server offers when two machines hear the same call
  /* AI DRAFT */ sharedRoomOffer: '{names} さんと同じ会議にいるようです。共有ルームに参加して同じ文字起こしを見ますか？',
  /* AI DRAFT */ sharedRoomOfferUnnamed: '他の BrSE ユーザーと同じ会議にいるようです。',
  /* AI DRAFT */ sharedRoomRequest:
    '{names} さんと同じ会議にいるようです。共有ルームへの参加を申請しています。',
  /* AI DRAFT */ sharedRoomRequestUnnamed:
    '他の BrSE ユーザーと同じ会議にいるようです。共有ルームへの参加を申請しています。',
  /* AI DRAFT */ sharedRoomAwaitingApproval: 'ルーム内の参加者の承認を待っています…',
  /* AI DRAFT */ sharedRoomCancel: 'キャンセル',
  /* AI DRAFT */ sharedRoomAgree: '参加する',
  /* AI DRAFT */ sharedRoomDecline: '参加しない',
  /* AI DRAFT */ sharedRoomWaiting: '相手の応答を待っています…',
  /* AI DRAFT */ sharedRoomDeclined: '共有ルームには参加しません。',
  /* AI DRAFT */ sharedRoomFailed: '共有ルームに参加できませんでした。',
  /* AI DRAFT */ sharedRoomJoined: '{names} さんと共有ルームに参加しました',
  /* AI DRAFT */ sharedRoomJoinedUnnamed: '共有ルームに参加しました',
  /* AI DRAFT */ sharedRoomLeave: 'ルームを退出',
  /* AI DRAFT */ sharedRoomLeaveFailed: '共有ルームを退出できませんでした。',
  /* AI DRAFT */ sharedRoomNeverAsk: '共有ルームへの参加を尋ねない',
  /* AI DRAFT */ sharedRoomNeverAskHint: 'このブラウザに保存されます（アカウント単位ではありません）。',
  /* AI DRAFT */ sharedRoomDetectionTitle: '同じ会議が見つかりました',
  /* AI DRAFT */ sharedRoomDetectionBodyBefore: '翻訳品質を向上させるため、',
  /* AI DRAFT */ sharedRoomDetectionBodyAfter: 'に他の参加者と一緒に参加しますか？',
  /* AI DRAFT */ sharedRoomDetectionBodyNoTitle:
    '翻訳品質を向上させるため、他の参加者と共有ルームに参加しますか？',
  /* AI DRAFT */ sharedRoomSimilarContentBadge: '類似コンテンツ',
  /* AI DRAFT */ sharedRoomDetectionMeetingNamePlaceholder: '会議名',
  /* AI DRAFT */ sharedRoomDetectionStartedMeta: '開始 {started} · {languages}',
  /* AI DRAFT */ sharedRoomDetectionInfo:
    '共有ルームでは、BrSE.ai は全メンバーから最も明瞭な音声を翻訳入力に使用します。',
  /* AI DRAFT */ sharedRoomParticipantsHeading: '参加者 ({count})',
  /* AI DRAFT */ sharedRoomMeetingNameFallback: '会議',
  /* AI DRAFT */ sharedRoomMeetingShortLabel: 'Meeting#{id}',
  /* AI DRAFT */ sharedRoomUnnamedPeer: '別の参加者',
  /* AI DRAFT */ similarMeetingDetected: '類似の会議を検出',
  /* AI DRAFT */ sharedRoomJoinLink: '参加',
  /* AI DRAFT */ sharedRoomHeaderJoined: '共有ルーム · {title}',
  /* AI DRAFT */ sharedRoomMembersTitle: 'メンバー ({count})',
  /* AI DRAFT */ sharedRoomMemberHost: 'ホスト',
  /* AI DRAFT */ sharedRoomMemberYou: 'あなた',
  /* AI DRAFT */ sharedRoomMemberJoinedAt: '{time} に参加',
  /* AI DRAFT */ sharedRoomToastJoined: '他 {count} 人と共有ルームに参加しました。',
  /* AI DRAFT */ sharedRoomToastJoinedSolo: '共有ルームに参加しました。',
  /* AI DRAFT */ sharedRoomToastContinuing: '自分の会議を続けています。',
  /* AI DRAFT */ sharedRoomToastLeft: '共有ルームを退出しました。',
  /* AI DRAFT */ sharedRoomLeaveConfirmTitle: '共有ルームを退出しますか？',
  /* AI DRAFT */ sharedRoomLeaveConfirmBodyIntro: '次の共有ルームを退出し、',
  /* AI DRAFT */ sharedRoomLeaveConfirmBodyOutro: '自分の会議に戻ります。',
  /* AI DRAFT */ sharedRoomLeaveConfirmBodyNoTitle:
    'この共有ルームを退出し、自分の会議に戻ります。',
  /* AI DRAFT */ sharedRoomLeaveConfirmInRoom: 'このルーム',
  /* AI DRAFT */ sharedRoomLeaveConfirmWarning:
    '退出後、BrSE.ai はあなたの音声のみを翻訳に使用します。',
  /* AI DRAFT */ sharedRoomSubtitleFallback:
    '共有ルーム · 全参加者の最良の音声',
  /* AI DRAFT */ sharedRoomYes: 'はい',
  /* AI DRAFT */ sharedRoomNo: 'いいえ',
  /* AI DRAFT */ sharedRoomProjectToastManual:
    '{name} さんがルームのプロジェクトを {project} に変更しました。',
  /* AI DRAFT */ sharedRoomProjectNoticeMajority:
    '共有ルームはプロジェクト {project}（多数決）を使用しています。あなたの会議も {project} に移ります。',
  /* AI DRAFT */ sharedRoomProjectNoticeTitle:
    '共有ルームはプロジェクト {project}（会議名）を使用しています。あなたの会議も {project} に移ります。',
  /* AI DRAFT */ sharedRoomProjectNoticeAccess:
    '共有ルームはプロジェクト {project}（Backlog 権限）を使用しています。あなたの会議も {project} に移ります。',
  /* AI DRAFT */ sharedRoomProjectNoticeFirstJoined:
    '共有ルームはプロジェクト {project}（先に参加）を使用しています。あなたの会議も {project} に移ります。',
  /* AI DRAFT */ sharedRoomProjectNoticeManual:
    '共有ルームはプロジェクト {project} を使用しています。あなたの会議も {project} に移ります。',
  /* AI DRAFT */ sharedRoomProjectRoomLabel: 'ルーム · {project}',
  /* AI DRAFT */ projectUpdateNotSupported:
    '共有ルームではプロジェクトを空にできません。',
  /* AI DRAFT */ projectUpdateNotReadable:
    'そのプロジェクトは Backlog で参照できません。ルームのプロジェクトは変わりません。',
  showBilingualTimeline: '高精度モードを表示',
  hideBilingualTimeline: '高精度モードを非表示',
  showInput: '入力を表示',
  hideInput: '入力を非表示',
  showOutput: '出力を表示',
  hideOutput: '出力を非表示',
  showLiveMemo: 'ライブメモを表示',
  hideLiveMemo: 'ライブメモを非表示',
  showOutputBatch: '出力（バッチ）を表示',
  hideOutputBatch: '出力（バッチ）を非表示',
  realtimeContextLabel: 'コンテキストを追加',
  realtimeContextPlaceholder: '例: スライド3を表示中：システムアーキテクチャ…（空欄で削除）',
  realtimeContextSubmit: 'コンテキストを保存',
  realtimeContextSaved: 'コンテキストを保存しました（{count}文字）',
  realtimeContextTooLong: 'コンテキストは{max}文字以内で入力してください。',
  realtimeContextUpdateFailed: 'コンテキストの更新に失敗しました',
  realtimeContextScopePrefix: '適用範囲: ',
  realtimeContextScopeSuffix: '。リアルタイム字幕には影響しません。',
  realtimeContextEmptyHint: 'AIが正しく認識すべき用語、プロジェクト名、人物名を入力してください',
  feedbackLabel: 'フィードバック',
  feedbackTooltip: 'ご意見・不具合はありませんか？お知らせください！',
  feedbackHeaderCompose: 'クイックフィードバック',
  feedbackHeaderList: 'マイフィードバック',
  feedbackPopoverPlaceholder: 'ご意見、不具合報告、ご提案などをお聞かせください…',
  feedbackSubmit: '送信',
  feedbackSubmitSuccess: 'フィードバックを送信しました！ありがとうございます 🙌',
  feedbackSubmitError: 'フィードバックの送信に失敗しました',
  feedbackSubmitFailedRetry: '送信に失敗しました。再試行しますか？',
  feedbackRetry: '再試行',
  feedbackReadOnly: '閲覧のみ',
  feedbackNewFeedback: '+ 新しいフィードバック',
  waitingSource: '入力を待機中…',
  waitingTranslation: '翻訳を待機中…',
  startSpeakingTitle: '話し始めると文字起こしが表示されます',
  startSpeakingBody: '原文と翻訳が話者ごとの会話カードにまとめて表示されます。',
  /* AI DRAFT */ enhancedWaitingTitle: '待機中...',
  /* AI DRAFT */ enhancedWaitingBody:
    '発言があると翻訳が表示されます。拡張モードの結果は10〜30秒かかることがあります。',
  jumpToLive: '↓ ライブへ移動',
  transcriptAria: '2列の文字起こし: 入力と翻訳',
  hideMeetingFooter: '操作バーを非表示',
  showMeetingFooter: '操作バーを表示',

  copilotLabel: 'Copilot',
  copilotTitle: 'AI Copilot',
  copilotListening: '聞いています',
  copilotSuggestionsTitle: '会議からの提案',
  copilotFilterWeb: 'Web検索',
  copilotFilterBacklog: 'Backlog',
  copilotSearchFilterLabel: '検索カテゴリ',
  copilotRefresh: '更新',
  copilotAnswerHeading: '回答',
  copilotSearchingAnswer: '回答を検索中…',
  copilotInputPlaceholder: 'Copilotに何でも聞いてください',
  /* AI DRAFT */ copilotResizeLabel: 'ドラッグして Copilot パネルの幅を変更',
  copilotSourceWeb: 'Web',
  copilotSourceBacklog: 'Backlog',
  copilotSourceMeeting: '過去の会議',
  copilotReferenceDetails: '詳細',
  copilotTagTechnical: '技術',
  copilotCloseAria: 'Copilotを閉じる',
  copilotEmpty: '提案や回答がここに表示されます。',
  /* AI DRAFT */ copilotYourQuestionsTitle: 'あなたの質問',

  assistPanelEmpty: '提案や回答がここに表示されます。',
  assistSources: '情報源',
  assistAnswerPending: '「{question}」を調べています...',
  assistAnswerFailed: '回答が見つかりませんでした。',
  /* AI DRAFT */ assistNeedsBacklogConnection:
    'Backlog に関する質問にはご自身の Backlog アカウントが必要です。設定で接続してから、もう一度お尋ねください。',
  /* AI DRAFT */ assistBacklogUnavailable:
    'このサーバーでは Backlog の参照が有効になっていません。',
  /* AI DRAFT */ assistBacklogNoProjects:
    'Backlog アカウントは接続済みですが、ここに同期されているプロジェクトに参加していません。',
  /* AI DRAFT */ assistBacklogNoMeetingProject:
    'この会議には Backlog プロジェクトが設定されていません。会議にプロジェクトを設定するか、質問にチケットキーを含めてください。',
  /* AI DRAFT */ assistBacklogNotMeetingMember:
    'この会議の Backlog プロジェクトを参照する権限がありません。チケットキーを指定するか、プロジェクトへの追加を依頼してください。',
  /* AI DRAFT */ assistBacklogProjectNotSynced:
    'このプロジェクトは Brse.AI に同期されていません。別のプロジェクトを選ぶか、チケット番号を添えて質問してください。',
  /* AI DRAFT */ assistBacklogScopeFailed:
    '参加している Backlog プロジェクトを確認できませんでした。もう一度お尋ねください。',
  /* AI DRAFT */ assistOpenSettingsNewTab: 'Backlog を接続（新しいタブで開きます）',

  /* AI DRAFT */ copilotSetupConnectTitle: 'チケットについて質問するには Backlog を接続してください',
  /* AI DRAFT */ copilotSetupConnectBody:
    'Copilot はご自身の Backlog アカウントでチケットを参照します。接続中も会議の録音は続きます。',
  /* AI DRAFT */ copilotSetupConnectAction: 'Backlog を接続',
  /* AI DRAFT */ copilotSetupReauthTitle: 'Backlog を再接続',
  /* AI DRAFT */ copilotSetupReauthBody:
    'Backlog の接続が無効になりました。再接続するまでチケットに関する質問には回答できません。',
  /* AI DRAFT */ copilotSetupReauthAction: 'Backlog を再接続',
  /* AI DRAFT */ copilotSetupProjectWarning:
    'Backlog プロジェクトが未設定です。選択すると、チケットについて回答できるようになります。',
  /* AI DRAFT */ copilotSetupApplying: '適用しています...',
  /* AI DRAFT */ copilotSetupApplied: 'Copilot は {project} を使用しています。',
  /* AI DRAFT */ copilotSetupApplyFailed:
    'そのプロジェクトを適用できませんでした。もう一度お試しください。',
  /* AI DRAFT */ copilotSetupUnsupported:
    '会議には保存しましたが、この録音は引き続き以前のプロジェクトを使用しています。',
  /* AI DRAFT */ copilotSetupConnected:
    'Backlog を接続しました。チケットに関する質問に回答できます。',
  /* AI DRAFT */ copilotSetupDismiss: '閉じる',

  /* AI DRAFT */ assistModelLabel: 'Copilot のモデル',
  /* AI DRAFT */ assistModelGemini: 'Gemini',
  /* AI DRAFT */ assistModelGpt: 'GPT',
  /* AI DRAFT */ assistModelGeminiHint: 'より高速',
  /* AI DRAFT */ assistModelGptHint: 'より低コスト',
  /* AI DRAFT */ assistModelUnavailable: 'このサーバーにはありません',

  /* AI DRAFT */ backlogConnectDialogTitle: 'Backlog を接続',
  /* AI DRAFT */ backlogConnectDialogBody:
    '承認は別ウィンドウで開きます。このタブの録音は続くため、内容は失われません。',
  /* AI DRAFT */ backlogConnectDialogAction: 'Backlog に進む',
  /* AI DRAFT */ backlogConnectDialogWaiting: 'Backlog のウィンドウでの承認をお待ちしています...',
  /* AI DRAFT */ backlogConnectDialogBlocked:
    'ブラウザーがウィンドウをブロックしました。このサイトのポップアップを許可するか、ご自身で承認画面を開いてください。',
  /* AI DRAFT */ backlogConnectDialogOpenManually: '承認画面を開く',
  /* AI DRAFT */ backlogConnectDialogFailed: 'Backlog の承認を開始できませんでした。',
  /* AI DRAFT */ backlogConnectDialogCancel: 'キャンセル',
  /* AI DRAFT */ backlogConnectDialogProjectTitle: 'この会議はどのプロジェクトのものですか？',
  /* AI DRAFT */ backlogConnectDialogProjectBody:
    'Copilot はこのプロジェクトのチケットについて回答します。あとからパネルで変更できます。',
  /* AI DRAFT */ backlogConnectDialogPickLater: '後で',
  /* AI DRAFT */ backlogConnectDialogProjectsFailed:
    'プロジェクトを読み込めませんでした。パネルから選択してください。',
  /* AI DRAFT */ backlogConnectDialogRetrying: '先ほどの質問をもう一度送信しています...',
  assistAskErrorBadRequest: '質問の送信に失敗しました。もう一度お試しください。',
  assistAskErrorSessionNotReady:
    'Copilotに質問する前に、会議を開始し翻訳がライブになるまでお待ちください。',
  assistAskErrorNotSupported:
    'このセッションでは Copilot の Q&A が AI サービス側で有効になっていません。STT プロキシで assist の answer flow を有効にしてください（UI フラグのみでは不十分です）。',
  /* AI DRAFT */ featuresAssistUnavailable: 'サーバーでアシストパネルがオフになっています。',
  /* AI DRAFT */ featuresUpdateError: 'ライブパネルを更新できませんでした。もう一度お試しください。',
  assistConflictInMeeting: '会議での内容',
  assistConflictInBacklog: 'Backlog上の内容',
  assistTicketLink: 'Backlogで見る',
  /* AI DRAFT — needs a native-speaker review before release. */
  conflictValueUnset: '未設定',
  /* AI DRAFT — needs a native-speaker review before release. */
  conflictAlertTitle: '警告: 今話された{field}がBacklogの記録と異なります',
  /* AI DRAFT — needs a native-speaker review before release. */
  conflictAlertBody:
    '会議では{field}が{incoming}と話されましたが、Backlogのチケット{issueKey}には{current}と記録されています。',
  /* AI DRAFT — needs a native-speaker review before release. */
  conflictAlertDaysApart: '(差は{days}日)',
  /* AI DRAFT — needs a native-speaker review before release. */
  conflictAlertFieldDeadline: '期限',
  /* AI DRAFT — needs a native-speaker review before release. */
  conflictAlertFieldStatus: 'ステータス',
  /* AI DRAFT — needs a native-speaker review before release. */
  conflictAlertViewDetail: '詳細を見る',
  /* AI DRAFT — needs a native-speaker review before release. */
  conflictAlertDismiss: 'この警告を閉じる',

  durationWarningTitle: 'BrSE.aiを引き続き使用しますか?',
  durationWarningBody:
    'この会議は長時間続いています。応答がない場合、BrSE.aiは翻訳を一時停止します。',
  durationWarningYesContinue: '続ける',
  durationWarningNo: 'いいえ',
  durationWarningEndTitle: '会議を終了しますか?',
  durationWarningEndBody:
    'BrSE.aiは停止し、議事録画面に移動します。この操作は取り消せません。',
  durationWarningYesStop: 'はい、終了する',
  durationWarningNoGoBack: 'いいえ、戻る',

  howWasMeeting: '会議はいかがでしたか？',
  feedbackSubtitle: 'フィードバックは次回のAI翻訳の改善に役立ちます。',
  translationQuality: '翻訳品質',
  additionalComments: '追加コメント・フィードバック',
  feedbackPlaceholder: 'AIが見落とした用語、文化的ニュアンス、トーンなどがあれば教えてください。',
  submitFeedback: 'フィードバックを送信',
  skipFeedback: 'スキップ',
  rateStar: '{n}/5で評価',
  durationLessThanMin: '1分未満',

  login: 'ログイン',
  loggingIn: 'ログイン中…',
  loginSubtitle: 'React Plus Teamsアカウントでログイン',
  email: 'メールアドレス',
  password: 'パスワード',
  passwordPlaceholder: 'パスワードを入力',
  showPassword: 'パスワードを表示',
  hidePassword: 'パスワードを非表示',
  or: 'または',
  loginWithGoogle: 'Googleでログイン',
  loginWithMicrosoft: 'Microsoft Teamsでログイン',
  noAccount: 'アカウントをお持ちでない方は',
  register: '新規登録',
  invalidCredentials: 'メールアドレスまたはパスワードが正しくありません',
  emailRequired: 'メールアドレスを入力してください',
  emailInvalid: 'メールアドレスの形式が正しくありません',
  passwordRequired: 'パスワードを入力してください',
  registerTitle: '新規登録',
  registerSubtitle: 'React Plus Teamsアカウントを作成',
  haveAccount: 'すでにアカウントをお持ちの方は',
  registering: '登録中…',
  createAccount: 'アカウントを作成',
  name: 'お名前',
  namePlaceholder: '例: 山田 太郎',
  nameRequired: '名前を入力してください',
  nameMinLength: '名前は2文字以上で入力してください',
  confirmPassword: 'パスワード（確認用）',
  confirmPasswordPlaceholder: 'もう一度パスワードを入力',
  confirmPasswordRequired: '確認用パスワードを入力してください',
  passwordRequirements: '8文字以上、大文字・小文字・数字・記号を含む',
  passwordMinLength: 'パスワードは8文字以上で入力してください',
  passwordComplexity: 'パスワードは大文字・小文字・数字・記号をそれぞれ1文字以上含めてください',
  passwordsMustMatch: 'パスワードと確認用パスワードが一致しません',
  role: 'ロール',
  roleRequired: 'ロールを選択してください',
  roleDeveloper: '開発者 (Developer)',
  roleProjectManager: 'プロジェクトマネージャー (PM)',
  roleBrse: 'ブリッジSE (BrSE)',
  registrationSuccess: '登録が完了しました。ページを移動します…',
  emailAlreadyRegistered: 'このメールアドレスは既に登録されています',
  unexpectedError: '予期しないエラーが発生しました',
  loginInfoIncomplete: 'ログイン情報が不完全です',
  loginFailed: 'ログインに失敗しました',
  backToLogin: 'ログイン画面に戻る',
  processingLogin: 'ログイン処理中…',

  genericError: 'エラーが発生しました',

  networkError: 'サーバーに接続できません。ネットワークを確認してください。',
  authRequired: '認証が必要です。再度ログインしてください。',
  failedLoadMeetingList: 'ミーティング一覧の取得に失敗しました',
  failedRegister: '登録に失敗しました',
  failedLogin: 'ログインに失敗しました',
  failedMicrosoftLogin: 'Microsoft SSO ログインに失敗しました',
  failedGoogleLogin: 'Google SSO ログインに失敗しました',
  sonioxTokenInvalid: 'Soniox トークンの応答が不正です',
  sonioxTokenFailed: 'Soniox トークンの取得に失敗しました',
  failedValidateMeetingTitle: '会議タイトルの検証に失敗しました',
  failedDeleteMeetingService: '会議の削除に失敗しました',
  failedRestoreMeetingService: '会議の復元に失敗しました',
  failedPermanentDeleteMeetingService: '会議の完全削除に失敗しました',
  failedPermanentDeleteMeetingsService: '会議の一括完全削除に失敗しました',
  failedLoadMeetingDetails: '会議詳細の読み込みに失敗しました',
  failedSaveMeetingService: '会議の保存に失敗しました',
  failedUpdateMeeting: '会議の更新に失敗しました',
  meetingTitleTakenError: 'この名前の会議はすでに存在します。別の名前を選択してください。',
  failedUploadMeetingAudio: '会議音声のアップロードに失敗しました',
  failedGenerateMeetingMinutes: '議事録の生成に失敗しました',
  failedTranslateMeetingMinutes: '議事録の翻訳に失敗しました',
  meetingMinutesTimedOut: '議事録の生成がタイムアウトしました',
  failedSummarizeMeetingMemo: '会議メモの要約に失敗しました',
  failedAnalyzeMeetingFiles: '会議ファイルの解析に失敗しました',
  failedLoadMemoInsights: 'AIインサイトの読み込みに失敗しました',
  failedStopRecording: '録音の停止に失敗しました',
  failedStartRecording: '録音の開始に失敗しました',
  failedStartAudioCapture: '音声キャプチャの開始に失敗しました',
  sttTokenInvalidResponse: 'STT トークンの応答に token または wsUrl が含まれていません',
  websocketError: 'WebSocket エラーが発生しました',
  sttInvalidToken: 'STT セッションが拒否されました：トークンが無効です',
  sttTokenExpired: 'STT セッションが拒否されました：トークンの有効期限が切れています',
  sttProviderUnavailable: 'STT プロバイダーが現在利用できません',
  sttInactivityTimeout: '30秒間操作がなかったため STT セッションが終了しました',
  sttProtocolViolation: 'プロトコル違反のため STT セッションが終了しました',
  sttInternalServerError: 'サーバー内部エラーのため STT セッションが終了しました',
  audioTrackMissing:
    '音声トラックが見つかりません。ブラウザのダイアログで「タブの音声を共有」にチェックを入れてください。',
  microphoneTrackMissing: 'マイクトラックが見つかりません。マイクの使用を許可してください。',
  microphoneTrackMissingShort: 'マイクトラックが見つかりません。',
  microphoneAccessEnded: 'マイクへのアクセスが終了しました。',
  screenShareStopped: '共有を停止しました。',
  captureLostDrainingTitle: '共有が停止しました',
  captureLostDrainingBody:
    '録音を終了しました。最後の翻訳を受信しています（最大1分ほどかかります）。',
  captureLostDoneTitle: '録音を停止しました',
  captureLostDoneBody: '「開始」を押すと再度共有して、この会議を続けられます。',
  mediaRecorderErrorTab: 'タブ音声キャプチャ中にオーディオキャプチャエラーが発生しました。',
  mediaRecorderErrorMic: 'マイクキャプチャ中にオーディオキャプチャエラーが発生しました。',
  missingTextToAnalyze: '解析するテキストがありません',
  noMessagesToSummarize: '要約するメッセージがありません',
  noMessagesToAnalyze: '解析するメッセージがありません',
  openAiErrorFmt: 'OpenAI エラー: {status} - {detail}',
}

export type UiStrings = { [K in keyof typeof en]: string }

export const UI_STRINGS: Record<UiLocale, UiStrings> = { en, ja }

export function sanitizeUiLocale(value: unknown, fallback: UiLocale = 'en'): UiLocale {
  return value === 'ja' || value === 'en' ? value : fallback
}

export function fmt(template: string, vars: Record<string, string | number> = {}): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => String(vars[key] ?? `{${key}}`))
}
